/**
 * INVENTAIRES (§6.3) — les campagnes du magasin, et le rapprochement de celle qu'on ouvre.
 *
 * Ce que la page SERT : la liste des campagnes avec leur avancement, l'ouverture d'une campagne, le
 * geste qui FIGE le théorique, la saisie des comptages, et la validation qui écrit les ajustements.
 *
 * ⚠️ LE PHYSIQUE FAIT FOI, et cette page ne demande donc AUCUNE cause. C'est ce qui la distingue du
 * centre d'écarts, où un humain doit dire pourquoi avant qu'un mouvement s'écrive.
 *
 * ⚠️ « PAS ENCORE COMPTÉ » SE REND AUTREMENT QUE « ZÉRO », toujours. Les confondre ferait lire une
 * démarque totale sur un inventaire à moitié saisi — l'écran afficherait une perte massive et
 * parfaitement fausse (§23.0). D'où le tiret, la ligne grisée, et le compteur « non comptées ».
 *
 * ⚠️ DEUX GESTES IRRÉVERSIBLES y vivent — figer la photo, valider — et tous deux passent par une
 * confirmation qui nomme ce qui va se produire.
 */
import { useRef, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { TOAST_TIMING } from '@orgazone/ui-tokens';
import { confirm, EmptyState, Icon, Modal, Picker, ScanBlock, showToast, Tooltip, Trans, useLatest } from '@orgazone/ui-web';
import { formatMoney } from '@orgazone/money-format';
import { api, type InventoryCampaign, type InventoryDetail, type InventoryFacet, type InventoryReconRow, type InventorySearchResult, type InventoryZone } from '../api';
import { ProcesVerbal } from './inventaire-pv';
import { BlocAVenir } from './a-venir';
import { PanneauAide } from './aide';
import { fieldLabel } from '../vocabulary';
// ⚠️ LA CADENCE ET LE REPÈRE DE FRAÎCHEUR VIENNENT D'UNE SEULE RÈGLE : deux écrans les veulent
//    (l'inventaire, les réceptions), donc ils ne les décident plus chacun de leur côté (règle de 2).
import { CADENCE_RAFRAICHISSEMENT_MS, derniereVerification } from './rafraichissement';
import {
  avancementSession, ecartLisible, etatZone,
  heureDeReception, libelleEtatZone, quantiteLisible, signatureZone, VUE_SERVEUR, zonesArrivees,
  type VueRapprochement,
} from './inventaire-lecture';
import { key, t, type Translatable } from '../i18n';
import { reasonToSend, WRITTEN_REASONS } from '../written-reasons';

/** Lignes servies par page — la borne haute vit au serveur, celle-ci est ce que l'écran demande. */
const TAILLE_PAGE = 200;
/** Sur l'onglet Zones, aucune table n'est rendue : on ne demande qu'une ligne, pour les COMPTEURS. */
const TAILLE_ZONES = 1;



function Trait({ d, size = 15, stroke = 1.9 }: { d: string; size?: number; stroke?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  );
}

const dateHeure = (ms: number): string => new Date(ms).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/**
 * L'ÉTAT VIDE — il ENSEIGNE le déroulé, il ne se contente pas de dire « rien ».
 *
 * ⚠️ C'est l'écran qu'on voit le jour où on fait son premier inventaire, et c'est ce jour-là qu'on a
 * besoin de savoir que rien ne s'écrit avant la clôture. Un « aucune campagne » sec laisserait
 * quelqu'un ouvrir une campagne sans savoir qu'il peut continuer à vendre.
 */
function AucuneCampagne({ onCreer }: { onCreer: () => void }) {
  const etapes = [
    { n: '1', t: t('On compte'), d: t('douchette ou Mobile Partner, zone par zone — les ventes ne sont jamais bloquées.') },
    { n: '2', t: t('On compare'), d: t('écarts et jamais bipés, recomptage libre, pièce en main.') },
    { n: '3', t: t('On clôture'), d: t('les écritures partent, le procès-verbal est scellé.') },
  ];
  return (
    <div style={{ padding: '44px 24px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <span style={{ width: 52, height: 52, borderRadius: 'var(--r-md)', background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Trait d="M9 3h6v3H9zM6 5H5v16h14V5h-1M9 12l2 2 4-4.5" size={24} stroke={1.9} />
      </span>
      <div style={{ fontSize: 'var(--t-h2)', fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ink-900)' }}>{t('Aucun inventaire en cours')}</div>
      <p style={{ margin: '6px 0 18px', maxWidth: '46ch', textAlign: 'center', fontSize: 'var(--t-lead)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        <Trans k='Le stock se compte en session : on lance, on scanne zone par zone, on clôture. <1>Rien ne s’écrit dans le stock avant la clôture.</1>' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
      </p>
      <button onClick={onCreer}
        style={{ display: 'flex', alignItems: 'center', gap: 8, height: 44, padding: '0 22px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '14.5px', fontWeight: 700, cursor: 'pointer' }}>
        <Trait d="M12 5v14M5 12h14" size={15} stroke={2.4} />{t('Nouvel inventaire')}
      </button>
      <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', justifyContent: 'center', marginTop: 28, paddingTop: 20, borderTop: '1px dashed var(--ink-200)', width: '100%', maxWidth: 820 }}>
        {etapes.map((e) => (
          <div key={e.n} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', maxWidth: 240 }}>
            <span style={{ width: 21, height: 21, borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-meta)', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{e.n}</span>
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--ink-900)' }}>{e.t}</strong> — {e.d}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── LA PAGE ────────────────────────────────────────────────────────────────────────────────────
export function Inventaires() {
  const [rows, setRows] = useState<InventoryCampaign[] | null>(null);
  const [kinds, setKinds] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);
  /**
   * L AIDE, OUVERTE DEPUIS LE WIZARD.
   * ⚠️ Le meme panneau que celui du rail : deux aides du meme sujet finiraient par diverger, et
   * l une des deux dirait une regle qui n existe plus.
   */
  const [aideOuverte, setAideOuverte] = useState(false);
  const [histoire, setHistoire] = useState(false);
  const [pvOuvert, setPvOuvert] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setFailure(null);
      const r = await api.inventories();
      setRows(r.rows);
      setKinds(r.kinds);
    } catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  /**
   * ⚠️ UN SEUL INVENTAIRE VIVANT À LA FOIS, ET LA PAGE EST CET INVENTAIRE (maquette, décision
   * utilisateur 2026-09-03). Il n'y a pas de LISTE de campagnes ouvertes : la div « nouvel inventaire »
   * BASCULE vers la session dès qu'on en lance un, et redevient elle-même à la clôture.
   *
   * ⚠️ CE N'EST PAS QU'UNE MISE EN PAGE. Deux campagnes vivantes figeraient chacune sa photo et
   * écriraient chacune ses ajustements à la clôture : le même manque serait constaté DEUX FOIS, et
   * corrigé deux fois. C'est le serveur qui l'interdit désormais (`createCampaign`) ; l'écran n'a donc
   * jamais à choisir laquelle montrer.
   *
   * ⚠️ ET PLUS DE `?open=` : la campagne vivante s'affiche d'elle-même, donc un rafraîchissement
   * retombe dessus par construction. Le paramètre d'URL n'avait de sens que face à une liste.
   */
  // ⚠️ « VIVANTE » SE DIT EN POSITIF : préparée ou en cours. Écrit en négatif (« pas clôturée »), le
  //    filtre a laissé passer l'abandon le jour où le statut est apparu — une campagne jetée serait
  //    revenue occuper l'écran, et le bouton « Nouvel inventaire » aurait disparu avec elle.
  const courante = useMemo(() => (rows ?? []).find((c) => c.status === 'prepared' || c.status === 'running') ?? null, [rows]);
  const passees = useMemo(() => (rows ?? []).filter((c) => c.status === 'closed' || c.status === 'abandoned'), [rows]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Inventaires')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='On compte ce qu’on voit — jamais l’inverse. <1>L’écart s’explique, la correction s’écrit</1>, le stock repart juste.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        {/* ⚠️ L'HISTORIQUE SE REPLIE DEPUIS L'EN-TÊTE (maquette) : il s'accumule année après année, et
            déplié en permanence il noierait la seule chose qui demande une action — l'inventaire du
            jour. Le bouton dit son ÉTAT, pas son action, pour qu'on sache ce qu'on regarde. */}
        {/* ⚠️ IL RESTE VISIBLE MÊME À ZÉRO (maquette) : « Historique (0) » dit qu'aucun inventaire n'a
            jamais été clos ici — le cacher laisserait croire que la fonction n'existe pas. */}
        <button onClick={() => setHistoire((v) => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, height: 38, padding: '0 14px', border: `1px solid ${histoire ? 'var(--ink-900)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: histoire ? 'var(--ink-900)' : 'var(--paper)', color: histoire ? 'var(--paper)' : 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <Trait d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2" size={14} stroke={2} />
          {histoire ? t('Masquer l’historique') : t('Historique ({count})', { count: passees.length })}
        </button>
      </div>

      {failure ? (
        <div style={{ padding: '14px 16px', marginBottom: 12, fontSize: '12.5px', color: 'var(--rose-fg)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', lineHeight: 1.55 }}>
          {t('Les inventaires n’ont pas pu être lus — {failure}', { failure })}
        </div>
      ) : rows === null ? (
        <div style={{ padding: '14px 16px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture des inventaires…')}</div>
      ) : courante ? (
        <Campagne id={courante.id} kinds={kinds} onCloture={reload} />
      ) : (
        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', marginBottom: 12 }}>
          <AucuneCampagne onCreer={() => setCreation(true)} />
        </div>
      )}

      {histoire ? <InventairesPasses rows={passees} kinds={kinds} onRapport={setPvOuvert} /> : null}

      {creation ? <ModaleCreation onAide={() => setAideOuverte(true)} onClose={() => setCreation(false)} onCree={async () => { setCreation(false); await reload(); }} /> : null}
      {pvOuvert ? <ProcesVerbal campaignId={pvOuvert} onClose={() => setPvOuvert(null)} /> : null}
      {aideOuverte ? <PanneauAide pathname="/inventaires" procDirecte onClose={() => setAideOuverte(false)} /> : null}
    </div>
  );
}

/**
 * LES INVENTAIRES PASSÉS — verrouillés, consultables, jamais modifiables.
 *
 * ⚠️ UNE LIGNE OUVRE SON RAPPORT, PAS UNE SESSION. Un inventaire clos n'a plus rien à compter : lui
 * rouvrir un écran de saisie proposerait des gestes qui n'aboutiraient à rien. Ce qu'on vient y
 * chercher, c'est le document — daté, chiffré, opposable.
 */
function InventairesPasses({ rows, kinds, onRapport }: {
  rows: InventoryCampaign[]; kinds: Record<string, string>; onRapport: (id: string) => void;
}) {
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--ink-100)' }}>
        <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)', flex: 1 }}>{t('Inventaires passés')}</span>
        <span style={{ fontSize: '11.5px', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t('Verrouillés — consultables, jamais modifiables')}</span>
      </div>
      <div style={{ minWidth: 840 }}>
        <div style={{ display: 'grid', gridTemplateColumns: COLONNES_HISTORIQUE, gap: 10, padding: '8px 16px', borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
          <div>{t('Réf')}</div><div>{t('Date')}</div><div>{t('Type')}</div><div>{t('Périmètre')}</div>
          <div style={{ textAlign: 'right' }}>{t('Références')}</div>
          <div style={{ textAlign: 'right' }}>{t('Écarts')}</div>
          <div style={{ textAlign: 'right' }}>{t('Ajusté (PMP)')}</div>
          <div>{t('Statut')}</div>
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: '22px 16px', fontSize: '12.5px', color: 'var(--ink-500)', textAlign: 'center' }}>
            
            {t('Aucun inventaire clôturé dans ce magasin.')}
          </div>
        ) : rows.map((c) => <LigneHistorique key={c.id} c={c} kinds={kinds} onRapport={onRapport} />)}
      </div>
    </div>
  );
}

/** Une campagne close, en une ligne — et le bouton qui ouvre son document. */
function LigneHistorique({ c, kinds, onRapport }: {
  c: InventoryCampaign; kinds: Record<string, string>; onRapport: (id: string) => void;
}) {
  const abandonnee = c.status === 'abandoned';
  const ecarts = abandonnee ? null : c.varianceCount;
  const perte = (c.deltaValueCents ?? 0) < 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: COLONNES_HISTORIQUE, gap: 10, padding: '9px 16px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center', fontSize: '12.5px' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--ink-700)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.id}</div>
      <div style={{ color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{c.closedAt ? dateHeure(c.closedAt) : '—'}</div>
      <div>
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 21, padding: '0 8px', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: 'var(--t-micro)', fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{kinds[c.kind] ?? c.kind}</span>
      </div>
      <div style={{ color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{perimetreLisible(c)}</div>
      {/* ⚠️ UNE CAMPAGNE ABANDONNÉE N'A PLUS DE PHOTO : elle a été jetée avec le reste. On montre donc
          ce qu'elle a COÛTÉ — les références comptées au moment du geste — plutôt qu'un 0 qui ferait
          lire « inventaire vide » là où il y a eu du travail. */}
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>
        {abandonnee ? c.discardedCount : c.snapshotCount}
      </div>
      {/* ⚠️ PAS DE PROCÈS-VERBAL, PAS DE CHIFFRE. Un « 0 » ici se lirait « inventaire sans le moindre
          écart » — le résultat le plus rassurant qui soit, et le plus faux (§23.0). */}
      <div style={{ textAlign: 'right' }}>
        {ecarts == null ? <span style={{ color: 'var(--ink-400)' }}>—</span> : (
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 21, padding: '0 8px', borderRadius: 'var(--r-xs)', fontSize: 'var(--t-meta)', fontWeight: 800, fontVariantNumeric: 'tabular-nums', background: ecarts > 0 ? 'var(--amber-bg)' : 'var(--teal-100)', color: ecarts > 0 ? 'var(--amber-fg)' : 'var(--teal-800)' }}>{ecarts}</span>
        )}
      </div>
      {/* ⚠️ UN ABANDON N'A AJUSTÉ RIEN DU TOUT : « 0,00 € » se lirait « inventaire juste », alors que
          rien n'a été comparé. Le tiret dit l'absence, il ne la chiffre pas (§23.0). */}
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: abandonnee ? 'var(--ink-400)' : perte ? 'var(--rose-fg)' : 'var(--ink-700)' }}>
        {abandonnee ? '—' : ecartLisible(c.deltaValueCents)}
      </div>
      {/* ⚠️ UN INVENTAIRE ABANDONNÉ N'A PAS DE RAPPORT, et le bouton n'est donc PAS offert : aucun
          procès-verbal n'a été scellé, il n'y a rien à réimprimer. Le proposer ouvrirait un document
          vide, ou un échec — les deux feraient croire à une perte. */}
      {abandonnee ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 21, padding: '0 8px', borderRadius: 'var(--r-xs)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-micro)', fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            
            {t('Abandonné')}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.closedBy ?? ''}</span>
        </div>
      ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Tooltip label={t('campagne close')} content={t('Verrouillée — consultable, jamais modifiable.')}>
          <span style={{ display: 'flex', color: 'var(--ink-400)' }}>
            <Trait d="M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8 11V7a4 4 0 0 1 8 0v4" size={13} stroke={2} />
          </span>
        </Tooltip>
        <button onClick={() => onRapport(c.id)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, height: 30, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          <Trait d="M7 17h10v5H7zM7 12H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 01-2 2h-2M7 12h10v5H7z" size={12} stroke={2} />
          
          {t('Rapport')}
        </button>
      </div>
      )}
    </div>
  );
}

/** La grille du tableau d'historique — relevée de la maquette, à la colonne près. */
const COLONNES_HISTORIQUE = '118px 92px 108px minmax(0,1.3fr) 96px 96px 110px 150px';

/**
 * LA TEINTE DU BADGE DE TYPE — relevée de la maquette (`INVTC`).
 *
 * ⚠️ LE TYPE EST L'INFORMATION LA PLUS STRUCTURANTE de l'en-tête : un complet remet à zéro tout ce
 * qu'il n'a pas vu, un tournant ne touche que son périmètre. Une pastille grise pour les deux ferait
 * lire la même conséquence.
 */
const TONS_TYPE: Record<string, [string, string]> = {
  // ⚠️ Un type inconnu retombe sur l'encre neutre plutôt que de casser : le vocabulaire des types vient
  //    du serveur (`kinds`), donc l'écran doit survivre à celui qu'on ajoutera demain.
  // ⚠️ LES CINQ TYPES OFFERTS ONT CHACUN LA LEUR (maquette : complet, tournant, ciblé, fournisseur, secteur) : la table
  //    ne connaissait que les trois anciens, et trois des cinq types du wizard s'affichaient en gris — la confusion même
  //    que cette teinte existe pour éviter. « Par marque » a remplacé « par fournisseur » : il en prend l'ambre.
  full: ['var(--violet-bg)', 'var(--violet-fg)'],
  rolling: ['var(--teal-100)', 'var(--teal-800)'],
  targeted: ['var(--blue-bg)', 'var(--blue-fg)'],
  brand: ['var(--amber-bg)', 'var(--amber-fg)'],
  sector: ['var(--ink-100)', 'var(--ink-700)'],
  // Les hérités gardent la teinte de leur successeur, pour qu'une ligne d'historique se lise comme aujourd'hui.
  supplier: ['var(--amber-bg)', 'var(--amber-fg)'],
  partial: ['var(--teal-100)', 'var(--teal-800)'],
  spot: ['var(--blue-bg)', 'var(--blue-fg)'],
};
const tonType = (kind: string): [string, string] => TONS_TYPE[kind] ?? ['var(--ink-100)', 'var(--ink-700)'];

/**
 * LA LIGNE DE CONTEXTE de l'en-tête : périmètre, moment de la photo, qui a ouvert.
 *
 * ⚠️ « PAS ENCORE FIGÉ » SE DIT, il ne se tait pas : c'est l'unique différence entre une campagne
 * préparée et une campagne en cours, et c'est elle qui décide si un comptage a un sens.
 */
function metaSession(c: InventoryCampaign): string {
  const quand = c.startedAt ? t('théorique figé le {v}', { v: dateHeure(c.startedAt) }) : t('théorique pas encore figé');
  // ⚠️ LA DATE VISÉE NE SE MONTRE QUE SI ELLE EXISTE (SPEC §3) : « clôture visée le — » sur une
  //    campagne héritée, qui n'en portait pas, ferait lire une échéance manquante plutôt qu'absente.
  //    ⚠️ DEUX PHRASES ENTIÈRES : la mention passait en VALEUR, un morceau de prose que le traducteur ne voyait jamais.
  const vars = { v: perimetreLisible(c), quand, createdBy: c.createdBy };
  return c.targetDate
    ? t('{v} · {quand} · clôture visée le {targetDate} · ouvert par {createdBy}', { ...vars, targetDate: c.targetDate })
    : t('{v} · {quand} · ouvert par {createdBy}', vars);
}

/** Le périmètre en une ligne — « Tout le magasin » quand il n'y en a pas, jamais un vide. */
function perimetreLisible(c: InventoryCampaign): string {
  const parts = Object.values(c.scope ?? {}).flatMap((v) => v ?? []);
  return parts.length ? parts.join(', ') : t('Tout le magasin');
}

// ── OUVRIR UNE CAMPAGNE ────────────────────────────────────────────────────────────────────────
/** ⚠️ Au niveau MODULE : définie pendant le rendu, React démonterait son sous-arbre à chaque frappe. */
/**
 * LES CINQ TYPES D'INVENTAIRE, et ce que chacun demande ensuite (SPEC §2, maquette invFTypes).
 *
 * ⚠️ CHAQUE TYPE PORTE SA DESCRIPTION, pas seulement son nom. « Tournant » ne dit rien à qui n'a
 * jamais fait d'inventaire ; « une ou plusieurs familles, sans fermer » se choisit tout seul.
 */
const TYPES_INVENTAIRE: { v: string; l: Translatable; d: Translatable; ic: string }[] = [
  { v: 'full', l: key('Complet'), d: key('Tout le magasin. Le stock repart juste de A à Z.'),
    ic: 'M5 3h14a1 1 0 0 1 1 1v17H4V4a1 1 0 0 1 1-1zM9 7h2M13 7h2M9 11h2M13 11h2M9 15h6M9 19h6' },
  { v: 'rolling', l: key('Tournant'), d: key('Une ou plusieurs familles, sans fermer. Un peu chaque mois.'),
    ic: 'M20 12a8 8 0 11-2.34-5.66M20 3v4h-4' },
  { v: 'targeted', l: key('Ciblé'), d: key('Quelques articles précis, après un doute ou une alerte.'),
    ic: 'M12 2v3M12 19v3M2 12h3M19 12h3M12 7a5 5 0 100 10 5 5 0 000-10' },
  { v: 'brand', l: key('Par marque'), d: key('Tout ce qui porte une marque — avant un retour ou un litige.'),
    ic: 'M1 8h12v8H1zM13 11h4l3 3v2h-7M5.2 18.5a1.6 1.6 0 100-3.2 1.6 1.6 0 000 3.2zM16.8 18.5a1.6 1.6 0 100-3.2 1.6 1.6 0 000 3.2z' },
  { v: 'sector', l: key('Par secteur'), d: key('Un pan du catalogue — enfant, femme, accessoires…'),
    ic: 'M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z' },
];

/** La question posée à chaque étape — elle change avec le type, parce que le périmètre change. */
const TITRE_ETAPE: Record<string, Translatable> = {
  type: key('Quel inventaire ?'), zones: key('Où compte-t-on ?'), date: key('Quand clôturer ?'),
  rolling: key('Quelles familles ?'), targeted: key('Quels articles ?'),
  brand: key('Quelles marques ?'), sector: key('Quels secteurs ?'),
};

/** Une pastille de choix — le même dessin pour une famille, une marque, un secteur ou une zone. */
function Puce({ texte, actif, onClick }: { texte: string; actif: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{
      height: 32, padding: '0 13px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
      border: `1.5px solid ${actif ? 'var(--teal-800)' : 'var(--ink-200)'}`,
      background: actif ? 'var(--teal-100)' : 'var(--paper)',
      color: actif ? 'var(--teal-800)' : 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 700,
    }}>{texte}</button>
  );
}

/**
 * OUVRIR UNE CAMPAGNE — l'assistant en quatre temps au plus (SPEC §2, transposé de la maquette).
 *
 * ⚠️ LE NOMBRE D'ÉTAPES DÉPEND DU TYPE, et c'est le détail que la prose du SPEC ne dit pas : un
 * inventaire COMPLET saute l'étape de périmètre — il n'y en a pas à choisir. Poser la question quand
 * même ferait répondre « tout » à une question qui n'en est pas une.
 *
 * ⚠️ CE QUI REMPLACE UN CHAMP LIBRE. La version précédente demandait les familles en TEXTE, séparées
 * par des virgules : une faute de frappe passait sans un mot et produisait un périmètre vide ou faux,
 * découvert seulement après avoir FIGÉ la photo — c'est-à-dire trop tard. Ici on choisit dans ce qui
 * existe, et le serveur ne retient que la dimension annoncée par le type.
 *
 * ⚠️ LE RÉCAPITULATIF VIT SUR LA DERNIÈRE ÉTAPE, pas sur une cinquième : on relit ce qu'on a répondu
 * au moment de décider, pas dans un écran de plus qui se saute.
 *
 * ⚠️ DÉFINIE AU NIVEAU MODULE (§24.1) : définie pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe.
 */
function ModaleCreation({ onClose, onCree, onAide }: {
  onClose: () => void; onCree: (id: string) => Promise<void>;
  /** Ouvre l’aide de l’inventaire — le détail des règles vit là, pas dans le wizard. */
  onAide: () => void;
}) {
  const [type, setType] = useState('');
  const [choisis, setChoisis] = useState<string[]>([]);
  const [modeZones, setModeZones] = useState<'pre' | 'live'>('pre');
  const [zonesPre, setZonesPre] = useState<string[]>([]);
  const [zoneSaisie, setZoneSaisie] = useState('');
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');
  // ⚠️ CHAQUE LIBELLÉ DÉSIGNE SON CHAMP : posé à côté sans lien, il n'est lu par aucun lecteur d'écran et son clic ne
  //    mène nulle part — le champ de la date n'avait, pour une aide technique, aucun nom.
  const idDate = useId();
  const idNom = useId();
  const [i, setI] = useState(0);
  const [enCours, setEnCours] = useState(false);
  /** Les valeurs offertes pour la dimension du type — lues au catalogue, jamais tapées. */
  const [offres, setOffres] = useState<{ family: string[]; brand: string[]; sector: string[] } | null>(null);
  const [recherche, setRecherche] = useState('');
  const [suggestions, setSuggestions] = useState<{ gencode: string; name: string }[]>([]);
  /**
   * Où en est la recherche — sans lui, « en cours », « rien trouvé » et « refusée » rendaient la même absence de puces,
   * et un refus du serveur se lisait « cet article n'existe pas ».
   */
  const [recherchee, setRecherchee] = useState<{ etat: 'calme' | 'cherche' | 'trouve' | 'echec'; motif?: string; sansCode?: number }>({ etat: 'calme' });

  // ⚠️ UN COMPLET SAUTE L’ÉTAPE DE PÉRIMÈTRE : il n’y en a pas à choisir (maquette invNWSteps).
  const etapes = useMemo(() => (!type || type === 'full' ? ['type', 'zones', 'date'] : ['type', 'per', 'zones', 'date']), [type]);
  const etape = etapes[Math.min(i, etapes.length - 1)] ?? 'type';

  useEffect(() => {
    let vivant = true;
    void api.articleFacets()
      // ⚠️ LES FACETTES VIENNENT DU CATALOGUE — c est ce qui remplace le champ libre. On ne garde que
      //    la valeur ; leur poids ne sert pas ici.
      .then((f) => { if (vivant) setOffres({
        family: (f.values.fam ?? []).map((v) => v.value),
        brand: (f.values.marq ?? []).map((v) => v.value),
        sector: (f.values.sect ?? []).map((v) => v.value),
      }); })
      // ⚠️ Best-effort ASSUMÉ : sans les listes, les étapes concernées le DISENT (voir plus bas) —
      //    elles ne se remplissent pas d’un champ libre qui redonnerait le défaut qu’on vient de retirer.
      .catch(() => { if (vivant) setOffres({ family: [], brand: [], sector: [] }); });
    return () => { vivant = false; };
  }, []);

  // ⚠️ LA RECHERCHE D’ARTICLES EST TEMPORISÉE : une frappe par caractère lancerait sept requêtes dont
  //    six sont périmées avant d’arriver.
  // ⚠️ GARDE ANTI-RÉPONSE PÉRIMÉE : une réponse arrivée après une frappe plus récente montrerait des puces qui ne
  //    correspondent plus à ce qui est écrit dans le champ.
  useEffect(() => {
    if (type !== 'targeted' || recherche.trim().length < 2) { setSuggestions([]); setRecherchee({ etat: 'calme' }); return; }
    let vivant = true;
    setRecherchee({ etat: 'cherche' });
    const timer = setTimeout(() => {
      void api.articles({ q: recherche.trim(), page: 0, size: 8, sort: 'designation', dir: 'asc' })
        .then((r) => {
          if (!vivant) return;
          // ⚠️ UN INVENTAIRE COMPTE PAR CODE-BARRES : un article sans code ne s'ajoute pas — il est écarté, et ça se dit.
          const avecCode = r.rows.filter((a) => a.gencode).map((a) => ({ gencode: a.gencode!, name: a.name }));
          setSuggestions(avecCode);
          setRecherchee({ etat: 'trouve', sansCode: r.rows.length - avecCode.length });
        })
        .catch((e: unknown) => {
          if (!vivant) return;
          setSuggestions([]);
          setRecherchee({ etat: 'echec', motif: e instanceof Error ? e.message : String(e) });
        });
    }, 250);
    return () => { vivant = false; clearTimeout(timer); };
  }, [type, recherche]);

  const bascule = useCallback((v: string) => {
    setChoisis((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  }, []);

  /** Cette étape est-elle répondue ? ⚠️ Le bouton « Continuer » ne s’active pas avant (maquette). */
  const etapeOk = useCallback((id: string) => {
    if (id === 'type') return type !== '';
    if (id === 'per') return choisis.length > 0;
    if (id === 'zones') return modeZones === 'live' || zonesPre.length > 0;
    // ⚠️ La date est OBLIGATOIRE dans la maquette : un inventaire sans échéance ne se clôture jamais.
    return /^\d{4}-\d{2}-\d{2}$/.test(date);
  }, [type, choisis, modeZones, zonesPre, date]);

  /** Le titre de l'étape et le type choisi — des clés, résolues là où elles s'affichent. */
  const titreEtape = TITRE_ETAPE[etape === "per" ? type : etape];
  const typeChoisi = TYPES_INVENTAIRE.find((kind) => kind.v === type);
  const perimetreLisible = type === "full" ? t('Tout le magasin')
    : choisis.length === 0 ? "—"
    : type === "targeted" ? t('{n} article', { n: choisis.length })
    : choisis.join(", ");

  const lancer = useCallback(async () => {
    setEnCours(true);
    try {
      const dimension = { rolling: "family", targeted: "gencode", brand: "brand", sector: "sector" }[type];
      const scope: Record<string, string[]> = dimension ? { [dimension]: choisis } : {};
      // ⚠️ LE NOM PAR DÉFAUT EST ENREGISTRÉ : il se traduit dans la langue de qui crée, jamais une clé française.
      const nom = label.trim() || t(TYPES_INVENTAIRE.find((kind) => kind.v === type)?.l ?? key('Inventaire'));
      const c = await api.createInventory(nom, type, scope, date);

      // ⚠️ LES ZONES ANNONCÉES SONT VRAIMENT OUVERTES. Les collecter sans les créer ferait une étape
      //    qui ne sert à rien : on arriverait sur l'écran de comptage avec le placeholder « aucune
      //    zone », après en avoir nommé quatre. C'est l'interdit « option offerte non implémentée ».
      // ⚠️ ET LES ÉCHECS SE DISENT, un par un : la campagne EXISTE déjà à ce stade, donc on ne peut
      //    plus rien annuler — taire une zone manquante ferait biper dans une zone qu'on croit ouverte.
      const manquees: string[] = [];
      for (const z of (modeZones === "pre" ? zonesPre : [])) {
        try { await api.openInventoryZone(c.id, z); } catch { manquees.push(z); }
      }
      if (manquees.length > 0) {
        showToast(t('Campagne ouverte, mais {n} zone n’a pas pu être créée : {v}. Créez-la depuis le sélecteur de la barre de scan.', { n: manquees.length, v: manquees.join(", ") }), "warn", 9000);
      } else {
        showToast(t('Campagne ouverte — elle est préparée, rien n’est encore figé'), "ok");
      }
      await onCree(c.id);
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), "err", 7000); }
    finally { setEnCours(false); }
  }, [type, choisis, date, label, modeZones, zonesPre, onCree]);

  const dernier = i === etapes.length - 1;
  const offresDuType = !offres ? []
    : type === 'rolling' ? offres.family : type === 'brand' ? offres.brand
    : type === 'sector' ? offres.sector : [];

  return (
    <Modal title={t('Nouvel inventaire')} subtitle={t('Étape {v} / {total} · {v2}', { v: i + 1, total: etapes.length, v2: titreEtape ? t(titreEtape) : '' })}
      icon="box" tone="teal" width="lg" open onClose={onClose}>
      <div style={{ padding: "14px 18px" }}>
        {/* ⚠️ LA PROGRESSION EST VISIBLE DÈS LA PREMIÈRE ÉTAPE : on accepte de répondre à quatre
            questions quand on sait qu’il y en a quatre. */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {etapes.map((e, k) => (
            <span key={e} style={{ flex: 1, height: 3, borderRadius: 'var(--r-pill)', background: k <= i ? "var(--teal-800)" : "var(--ink-200)" }} />
          ))}
        </div>

        {etape === "type" ? (
          <>
            {/* ⚠️ CE QU’UN INVENTAIRE CHANGE PENDANT QU’IL TOURNE SE DIT AVANT DE LE LANCER, pas au
                moment où une réception est refusée. L’aide porte le détail ; ces deux lignes
                portent ce qu’on ne peut pas découvrir trop tard. */}
            <div style={{ marginBottom: 12, padding: "11px 13px", borderRadius: "var(--r-sm)", background: "var(--amber-bg)", color: "var(--amber-fg)", fontSize: "12.5px", lineHeight: 1.6 }}>
              
              <Trans k='Pendant l’inventaire, <1>aucune marchandise ne pourra entrer en stock</1> — ni réception, ni entrée manuelle, ni rétrocession. Un <2>inventaire complet</2> ne fige que les références vivantes : ce qui a du stock, et ce qui a bougé depuis le dernier complet.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} />{' '}<button type="button" onClick={onAide}
                style={{ border: "none", background: "none", padding: 0, font: "inherit", color: "inherit", textDecoration: "underline", cursor: "pointer" }}>
                
                {t('Lire l’aide de l’inventaire')}
              </button>.
            </div>
            <div style={{ display: "grid", gap: 8 }}>
            {TYPES_INVENTAIRE.map((kind) => (
              <button key={kind.v} type="button" onClick={() => { setType(kind.v); setChoisis([]); }}
                style={{ display: "flex", alignItems: "flex-start", gap: 11, padding: "12px 13px", textAlign: "left", cursor: "pointer",
                  border: `1.5px solid ${type === kind.v ? "var(--teal-800)" : "var(--ink-200)"}`,
                  borderRadius: "var(--r-md)", background: type === kind.v ? "var(--teal-100)" : "var(--paper)" }}>
                <span style={{ display: "flex", color: "var(--teal-800)", paddingTop: 1 }}>
                  <Trait d={kind.ic} size={16} stroke={1.9} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 'var(--t-title)', fontWeight: 700, color: "var(--ink-900)" }}>{t(kind.l)}</span>
                  <span style={{ display: "block", fontSize: 'var(--t-dense)', color: "var(--ink-500)", lineHeight: 1.5 }}>{t(kind.d)}</span>
                </span>
              </button>
            ))}
            </div>
          </>
        ) : etape === "per" ? (
          <div>
            {type === "targeted" ? (
              <>
                <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
                  placeholder={t('Cherchez un article ou un code, cliquez pour l’ajouter…')}
                  style={{ width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px", border: "1px solid var(--ink-200)", borderRadius: "var(--r-ctrl)", background: "var(--paper)", color: "var(--ink-900)", fontSize: 'var(--t-lead)', outline: "none" }} />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                  {suggestions.filter((a) => !choisis.includes(a.gencode)).map((a) => (
                    <Puce key={a.gencode} texte={`+ ${a.name}`} actif={false} onClick={() => bascule(a.gencode)} />
                  ))}
                </div>
                {recherchee.etat === 'cherche' ? (
                  <p style={{ margin: '8px 0 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Recherche…')}</p>
                ) : recherchee.etat === 'echec' ? (
                  <p role="alert" style={{ margin: '8px 0 0', fontSize: 'var(--t-dense)', color: 'var(--danger-ink)' }}>{t('La recherche a échoué : {motif}', { motif: recherchee.motif ?? '' })}</p>
                ) : recherchee.etat === 'trouve' && suggestions.length === 0 && !recherchee.sansCode ? (
                  <p style={{ margin: '8px 0 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Aucun article ne correspond à « {terme} ».', { terme: recherche.trim() })}</p>
                ) : null}
                {recherchee.etat === 'trouve' && recherchee.sansCode ? (
                  <p style={{ margin: '8px 0 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('{n} article sans code-barres écarté — un inventaire compte par code-barres.', { n: recherchee.sansCode })}</p>
                ) : null}
                {choisis.length > 0 ? (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--ink-100)" }}>
                    {choisis.map((g) => <Puce key={g} texte={`${g} ✕`} actif onClick={() => bascule(g)} />)}
                  </div>
                ) : null}
              </>
            ) : offresDuType.length > 0 ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {offresDuType.map((v) => <Puce key={v} texte={v} actif={choisis.includes(v)} onClick={() => bascule(v)} />)}
              </div>
            ) : (
              // ⚠️ ON DIT POURQUOI IL N’Y A RIEN À CHOISIR, plutôt que d’offrir un champ libre : c’est
              //    exactement le défaut qu’on vient de retirer, et il coûtait une photo figée à tort.
              <div style={{ fontSize: 'var(--t-body)', color: "var(--ink-500)", lineHeight: 1.6 }}>
                
                {t('Aucune valeur à proposer pour ce périmètre — le catalogue n’en porte pas encore, ou la lecture a échoué. Choisissez un autre type d’inventaire.')}
              </div>
            )}
          </div>
        ) : etape === "zones" ? (
          <div>
            {/* ⚠️ DEUX BOUTONS, PAS UNE CASE À COCHER : « au fil du scan » est une DÉCISION, pas
                l’absence de réponse. Une case décochée se lirait « je n’ai pas répondu ». */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {([["pre", t('Zones connues d’avance')], ["live", t('Créées au fil du scan')]] as const).map(([v, l]) => (
                <button aria-pressed={modeZones === v} key={v} type="button" onClick={() => setModeZones(v)}
                  style={{ flex: 1, height: 38, cursor: "pointer", fontSize: 'var(--t-body)', fontWeight: 700,
                    border: `1.5px solid ${modeZones === v ? "var(--teal-800)" : "var(--ink-200)"}`,
                    borderRadius: "var(--r-ctrl)", background: modeZones === v ? "var(--teal-100)" : "var(--paper)",
                    color: modeZones === v ? "var(--teal-800)" : "var(--ink-700)" }}>{l}</button>
              ))}
            </div>
            {modeZones === "pre" ? (
              <>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                  {zonesPre.map((z) => (
                    <Puce key={z} texte={`${z} ✕`} actif onClick={() => setZonesPre((p) => p.filter((x) => x !== z))} />
                  ))}
                </div>
                <input value={zoneSaisie} onChange={(e) => setZoneSaisie(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    const n = zoneSaisie.trim();
                    if (n && !zonesPre.includes(n)) setZonesPre((p) => [...p, n]);
                    setZoneSaisie("");
                  }}
                  placeholder={t('+ zone (Entrée)')}
                  style={{ width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px", border: "1px solid var(--ink-200)", borderRadius: "var(--r-ctrl)", background: "var(--paper)", color: "var(--ink-900)", fontSize: 'var(--t-lead)', outline: "none" }} />
              </>
            ) : (
              <div style={{ fontSize: 'var(--t-body)', color: "var(--ink-500)", lineHeight: 1.6 }}>
                
                {t('Les zones naîtront des comptages — au sélecteur de la barre de scan, ou poussées par un Mobile Partner.')}
              </div>
            )}
          </div>
        ) : (
          <div>
            <label htmlFor={idDate} style={{ display: "block", fontSize: 'var(--t-dense)', fontWeight: 700, color: "var(--ink-600)", marginBottom: 6 }}>
              {t('Date de clôture visée')}
            </label>
            <input id={idDate} type="date" value={date} onChange={(e) => setDate(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px", border: "1px solid var(--ink-200)", borderRadius: "var(--r-ctrl)", background: "var(--paper)", color: "var(--ink-900)", fontSize: 'var(--t-lead)', outline: "none" }} />
            <label htmlFor={idNom} style={{ display: "block", fontSize: 'var(--t-dense)', fontWeight: 700, color: "var(--ink-600)", margin: "14px 0 6px" }}>
              <Trans k='Nom de la campagne <1>— facultatif</1>' parts={[(x) => <span style={{ fontWeight: 500, color: "var(--ink-400)" }}>{x}</span>]} />
            </label>
            <input id={idNom} value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder={t(TYPES_INVENTAIRE.find((kind) => kind.v === type)?.l ?? key('Inventaire'))}
              style={{ width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px", border: "1px solid var(--ink-200)", borderRadius: "var(--r-ctrl)", background: "var(--paper)", color: "var(--ink-900)", fontSize: 'var(--t-lead)', outline: "none" }} />

            {/* ⚠️ LE RÉCAPITULATIF EST ICI, sur la dernière étape : on relit ce qu’on a répondu au
                moment de décider, pas dans un écran de plus qui se saute. */}
            <div style={{ marginTop: 16, border: "1px solid var(--ink-200)", borderRadius: "var(--r-sm)", overflow: "hidden" }}>
              {([
                [t('Type'), typeChoisi ? t(typeChoisi.l) : '—'],
                [t('Périmètre'), perimetreLisible],
                [t('Zones'), modeZones === "live" ? t('créées au fil du scan') : zonesPre.join(", ") || "—"],
                [t('Clôture visée'), date || "—"],
              ] as const).map(([k, v]) => (
                <div key={k} style={{ display: "flex", gap: 12, padding: "8px 12px", borderBottom: "1px solid var(--ink-100)", fontSize: 'var(--t-body)' }}>
                  <span style={{ color: "var(--ink-500)", width: 110, flexShrink: 0 }}>{k}</span>
                  <span style={{ color: "var(--ink-900)", fontWeight: 600, minWidth: 0, overflowWrap: "anywhere" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={enCours}
            style={{ height: 36, padding: "0 14px", border: "1px solid var(--ink-200)", borderRadius: "var(--r-ctrl)", background: "var(--paper)", color: "var(--ink-700)", fontSize: 'var(--t-lead)', fontWeight: 700, cursor: "pointer" }}>
            
            {t('Annuler')}
          </button>
          {i > 0 ? (
            <button onClick={() => setI((n) => n - 1)} disabled={enCours}
              style={{ height: 36, padding: "0 14px", border: "1px solid var(--ink-200)", borderRadius: "var(--r-ctrl)", background: "var(--paper)", color: "var(--ink-700)", fontSize: 'var(--t-lead)', fontWeight: 700, cursor: "pointer" }}>
              
              {t('Retour')}
            </button>
          ) : null}
          <span style={{ flex: 1 }} />
          {dernier ? (
            <button onClick={() => void lancer()} disabled={enCours || !etapeOk(etape)}
              style={{ height: 36, padding: "0 16px", border: "none", borderRadius: "var(--r-ctrl)", background: "var(--teal-800)", color: "#fff", fontSize: 'var(--t-lead)', fontWeight: 700, cursor: enCours || !etapeOk(etape) ? "default" : "pointer", opacity: enCours || !etapeOk(etape) ? 0.5 : 1 }}>
              {enCours ? t('Ouverture…') : t('Lancer l’inventaire')}
            </button>
          ) : (
            <button onClick={() => setI((n) => n + 1)} disabled={!etapeOk(etape)}
              style={{ height: 36, padding: "0 16px", border: "none", borderRadius: "var(--r-ctrl)", background: "var(--ink-900)", color: "var(--paper)", fontSize: 'var(--t-lead)', fontWeight: 700, cursor: etapeOk(etape) ? "pointer" : "default", opacity: etapeOk(etape) ? 1 : 0.4 }}>
              
              {t('Continuer')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * La carte d'une zone — reçue, en cours de comptage, ou silencieuse.
 *
 * ⚠️ AUCUN DÉCOMPTE PENDANT LE COMPTAGE. Un chiffre partiel serait FAUX : une zone scannée à 30 %
 * afficherait « 42 pièces », et l'onglet Écarts montrerait un manque massif sur ses articles — un
 * artefact produit par l'affichage. Les points disent la seule chose vraie à cet instant.
 * ⚠️ ANIMATION EN CSS, PAS EN MINUTEUR : la page reste ouverte des heures pendant un inventaire, et
 * un `setInterval` par carte y consommerait pour rien. Elle respecte `prefers-reduced-motion`.
 * ⚠️ INDÉTERMINÉE, jamais une barre qui se remplit : §23.0 interdit la progression au chronomètre.
 *    Trois points en boucle sont un témoin d'activité ; une barre à 60 % serait une invention.
 */
/**
 * L'ÉTAT D'UNE ZONE, tel que la carte et le picker le disent (SPEC-Inventaire §5).
 *
 * ⚠️ TROIS ÉTATS, ET « TERMINÉE » NE SE DÉDUIT PAS. Une zone qui porte trente pièces peut être bouclée
 * ou à moitié faite : seul un humain le sait, et c'est ce qu'il déclare. Le déduire du comptage ferait
 * annoncer « terminée » sur un rayon qu'on n'a fait qu'entamer — et on n'y reviendrait plus.
 * ⚠️ Une zone de TERMINAL est terminée dès qu'elle est soumise : là, c'est un fait, l'appareil a poussé
 * son comptage complet.
 */
/**
 * Les pièces qui ont bougé avant le passage du compteur — DEUX phrases entières.
 * ⚠️ Le participe passait en VALEUR (« sorties » / « entrées ») : il ne s'accordait jamais au nombre (« 1 pièce
 *    sorties »), et le traducteur recevait un mot sans sa phrase.
 */
const SORTIES_AVANT = key('{n} pièce sortie avant le comptage — le théorique retenu est {theoreticalAtCountMinor}.');
const ENTREES_AVANT = key('{n} pièce entrée avant le comptage — le théorique retenu est {theoreticalAtCountMinor}.');

function etatDeZone(z: InventoryZone): 'todo' | 'counting' | 'done' {
  // ⚠️ 🔴 « TERMINÉE » SE LIT, ELLE NE SE DÉDUIT PLUS. L'état d'une zone de terminal était déduit de
  //    « pas ouverte et pas du back-office » : l'affichage était juste, mais rien ne pouvait le
  //    DÉFAIRE — « Rouvrir » efface `finished_at`, et une déduction ne s'efface pas. La soumission
  //    écrit désormais ce fait, et le bouton a de nouveau prise dessus.
  if (z.finishedAt != null) return 'done';
  // ⚠️ UN APPAREIL QUI COMPTE EST « EN COURS », même à zéro pièce connue (vu au rendu, 2026-09-04) :
  //    il ne remonte RIEN avant d'avoir fini, donc son compteur vaut zéro alors que quelqu'un travaille.
  //    « À compter » enverrait un second compteur sur un rayon déjà pris en charge.
  if (!z.fromBackOffice && z.open) return 'counting';
  return z.piecesCounted > 0 ? 'counting' : 'todo';
}

const ETAT_ZONE: Record<'todo' | 'counting' | 'done', { l: Translatable; bg: string; fg: string; dot: string }> = {
  todo: { l: key('À compter'), bg: 'var(--ink-100)', fg: 'var(--ink-600)', dot: 'var(--ink-300)' },
  counting: { l: key('En cours'), bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', dot: 'var(--amber)' },
  done: { l: key('Terminée'), bg: 'var(--teal-100)', fg: 'var(--teal-800)', dot: 'var(--teal-500)' },
};

/**
 * LA CARTE D'UNE ZONE — son état, ses pièces, sa source, et le geste qui la concerne.
 *
 * ⚠️ LA CARTE ENTIÈRE OUVRE LE DÉTAIL (hors boutons) : c'est le geste naturel devant une tuile qui
 * annonce un nombre — « montre-moi ce qu'il y a dedans ». Réserver l'ouverture à un lien obligerait à
 * viser, sur un écran qu'on consulte la douchette en main.
 *
 * ⚠️ UNE ZONE DE TERMINAL EN COMPTAGE EST VERROUILLÉE (SPEC §5) : pas de « Scanner ici », un cadenas à
 * la place. Mélanger des bips de douchette dans un comptage d'appareil non transmis produirait un total
 * que ni l'un ni l'autre ne pourrait expliquer — et l'appareil écraserait tout à son envoi.
 */
function CarteZone({ zone: z, maintenant, onRattacher, active, onCompterIci, onRetirer, onTerminer, onOuvrir }: {
  zone: InventoryZone; maintenant: number; onRattacher: () => void;
  active: boolean; onCompterIci: () => void; onRetirer: () => void;
  onTerminer: (finished: boolean) => void; onOuvrir: () => void;
}) {
  const etat = etatDeZone(z);
  const ton = ETAT_ZONE[etat];
  // ⚠️ « EN COMPTAGE SUR L'APPAREIL » N'EST PAS « EN COURS » : rien n'a été transmis, donc on ne sait
  //    RIEN de son contenu. C'est la seule zone dont on ne peut pas dire le nombre de pièces.
  const enCoursAppareil = !z.fromBackOffice && z.open;
  /** Transmise par l'appareil, mais pas encore versée : elle n'apporte rien au rapprochement. */
  const recueNonVersee = !z.fromBackOffice && !z.open && !z.attached;
  const bord = active ? 'var(--teal-800)' : enCoursAppareil ? 'var(--teal-400)' : 'var(--ink-200)';

  return (
    // ⚠️ PAS DE `role="button"` SUR LA CARTE : elle porte ses contrôles (supprimer la zone, scanner ici), que le rôle
    //    rendrait présentationnels — invisibles pour une aide technique. Le NOM ouvre la zone au clavier, la carte
    //    entière à la souris.
    <div onClick={onOuvrir}
      style={{ border: `1.5px solid ${bord}`, borderRadius: 'var(--r-md)', background: 'var(--paper)', padding: '13px 15px', display: 'flex', flexDirection: 'column', gap: 9, cursor: 'pointer', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={(e) => { e.stopPropagation(); onOuvrir(); }}
          style={{ padding: 0, border: 'none', background: 'transparent', font: 'inherit', textAlign: 'left', cursor: 'pointer', fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.label}</button>
        {/* ⚠️ « ACTIVE » DIT OÙ PART LE PROCHAIN BIP, et c'est l'information la plus utile de l'écran
            quand on a la douchette en main. Elle passe donc devant l'état. */}
        {active ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 21, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--t-micro)', fontWeight: 800, whiteSpace: 'nowrap' }}>
            <span className="oz-pulse" style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: '#fff' }} />{t('Active')}
          </span>
        ) : null}
        <span style={{ display: 'inline-flex', alignItems: 'center', height: 21, padding: '0 8px', borderRadius: 'var(--r-xs)', background: ton.bg, color: ton.fg, fontSize: 'var(--t-micro)', fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{t(ton.l)}</span>
        {/* ⚠️ EN HAUT À DROITE ET EN ICÔNE, comme la maquette — un bouton « Retirer » en toutes lettres
            occupait la rangée d'actions et pesait autant que « Scanner ici », alors qu'il est le geste
            qu'on fait le moins. Discret au repos, il ne devient ROUGE qu'au survol : c'est là qu'on
            apprend qu'il détruit, au moment où on s'en approche.
            ⚠️ `stopPropagation` : la tuile entière ouvre la zone, et supprimer n'est pas ouvrir. */}
        <Tooltip label={t('Supprimer la zone')}>
          <button type="button" className="oz-zone-del" aria-label={t('Supprimer la zone {label}', { label: z.label })}
            onClick={(e) => { e.stopPropagation(); onRetirer(); }}
            style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: 'pointer' }}>
            <Icon name="trash" size={13} stroke={2} />
          </button>
        </Tooltip>
      </div>

      {enCoursAppareil ? (
        // ⚠️ AUCUN NOMBRE ICI, ET C'EST LE POINT : l'appareil compte hors ligne et ne remonte rien avant
        //    d'avoir fini. Afficher « 0 pièce » ferait lire un rayon vide (§23.0).
        // ⚠️ LES POINTS OCCUPENT LA PLACE DU NOMBRE, à sa taille — c'est ce qui garde toutes les
        //    tuiles à la même hauteur. Un texte court à la place du grand chiffre faisait une carte
        //    plus basse que ses voisines, et la grille perdait son alignement.
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span className="oz-points" aria-hidden="true"
            style={{ fontSize: 'var(--t-feature)', fontWeight: 800, color: 'var(--ink-900)', letterSpacing: '-0.02em' }}>
            <span>.</span><span>.</span><span>.</span>
          </span>
          <span style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>
            {libelleEtatZone(etatZone(z, maintenant), z.openedAt, maintenant)}
          </span>
        </div>
      ) : recueNonVersee ? (
        // ⚠️ PAS DE « 0 PIÈCES » AU-DESSUS DE « 42 LIGNES REÇUES » (vu au rendu) : les deux se
        //    contredisent à l'œil. Tant que rien n'est versé, la zone n'apporte RIEN au rapprochement —
        //    c'est vrai, mais ça se dit avec ce qu'elle apporterait, pas avec un zéro.
        <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--amber-fg)', lineHeight: 1.4 }}>
          {t('{n} pièce reçue, pas encore versée', { n: z.totalQtyMinor })}</div>
      ) : (
        // ⚠️ LE NOMBRE ENTRE DANS LA PHRASE : écrit à côté d'un « pièces » traduit seul, il affichait « 1 pièces » — et la
        //    clé portait le séparateur, qui appartient au code.
        <div style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>
          <Trans k='<1>{n}</1> pièce' vars={{ n: z.piecesCounted }}
            parts={[(x) => <span style={{ fontSize: 'var(--t-feature)', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', marginRight: 6 }}>{x}</span>]} />
          {' · '}{z.fromBackOffice ? t('douchette') : z.equipmentCode || t('appareil inconnu')}
        </div>
      )}

      {/* ⚠️ LES LIGNES REÇUES MAIS PAS ENCORE VERSÉES SE DISENT À PART : elles ne comptent pas dans le
          rapprochement tant que personne n'a versé, et le nombre au-dessus le reflète (il vaut 0). */}
      {recueNonVersee ? (
        <div style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>
          {t('{n} ligne', { n: z.lineCount })}{z.outOfScope > 0 ? <>{' · '}{t('{n} hors périmètre', { n: z.outOfScope })}</> : ''}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
        {recueNonVersee ? (
          <button onClick={onRattacher}
            style={{ flex: 1, minWidth: 120, height: 32, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
            
            {t('Verser dans le comptage')}
          </button>
        ) : null}
        {enCoursAppareil ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 120, height: 32, padding: '0 10px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', color: 'var(--ink-500)', fontSize: 'var(--t-dense)', fontWeight: 600 }}>
            <Trait d="M7 11h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8 11V7a4 4 0 0 1 8 0v4" size={13} stroke={2} />
            
            {t('Verrouillée — l’appareil compte')}
          </span>
        ) : etat === 'done' ? (
          // ⚠️ 🔴 « ROUVRIR » SUR TOUTE ZONE TERMINÉE, y compris venue d'un terminal. Une note écrite
          //    ici affirmait que le bouton « n'aurait rien à défaire » sur une zone soumise — c'était
          //    vrai du MÉCANISME d'alors (rien ne marquait la fin d'une zone de terminal) et faux du
          //    BESOIN : une zone transmise se complète très bien à la douchette, et le dessin l'offre.
          //    C'est le mécanisme qui a été corrigé, pas le besoin qui a été refusé.
          <button onClick={() => onTerminer(false)}
            style={{ flex: 1, minWidth: 110, height: 32, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
            
            {t('Rouvrir')}
          </button>
        ) : (
          <>
            {active ? null : (
              <button onClick={onCompterIci}
                style={{ flex: 1, minWidth: 110, height: 32, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
                
                {t('Scanner ici')}
              </button>
            )}
            <button onClick={() => onTerminer(true)}
              style={{ flex: 1, minWidth: 130, height: 32, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
              
              {t('Marquer terminée')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * CE QUE LES MOBILE PARTNER ONT TRANSMIS — une ligne par envoi, la plus récente en tête.
 *
 * ⚠️ IL MANQUAIT, et son absence se voyait : les tuiles disent l'ÉTAT de chaque zone, pas l'HISTOIRE
 * de ce qui est arrivé. Quand trois appareils poussent en vingt minutes, la grille se réorganise sans
 * qu'on sache jamais ce qui vient de tomber ni dans quel ordre.
 *
 * ⚠️ LES ZONES BIPÉES AU CLAVIER N'Y FIGURENT PAS. Ce panneau raconte ce qui arrive DU TERRAIN ; nos
 * propres saisies s'y liraient comme des réceptions, et le compteur en tête deviendrait faux.
 *
 * ⚠️ TROIS CHOSES DE LA MAQUETTE N'Y SONT PAS, ET C'EST DÉLIBÉRÉ :
 *   · « Simuler une transmission » — un artifice de prototype. Un bouton qui fabrique une réception
 *     est exactement l'interdit du §23.0 ;
 *   · le badge « zone créée auto » — nous ne savons PAS si l'appareil a créé la zone ou l'a trouvée.
 *     L'afficher au jugé serait une donnée inventée ;
 *   · le libellé « Mobile Scanner » — le produit s'appelle Mobile Partner depuis le renommage, et
 *     c'est le nom que porte le reste de cet écran.
 */
function PanneauReceptions({ zones }: { zones: InventoryZone[] }) {
  // ⚠️ RIEN À MONTRER N'EST PAS UN VIDE À MEUBLER : tant qu'aucun appareil n'a transmis, la grille de
  //    tuiles et son état vide disent déjà tout. Un panneau vide de plus ferait chercher une panne.
  const recues = useMemo(
    () => zones.filter((z) => !z.fromBackOffice && z.submittedAt != null)
      .sort((a, b) => (b.submittedAt ?? 0) - (a.submittedAt ?? 0)),
    [zones],
  );
  if (recues.length === 0) return null;

  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
          <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>
            
            {t('Mobile Partner — comptages reçus')}
          </span>
        </span>
        {/* ⚠️ LA MAQUETTE MET ICI UN POINT CLIGNOTANT ET « EN DIRECT ». On ne le reprend pas : la page
            INTERROGE le serveur, elle n'en est pas notifiée. Le repère « vérifié il y a … », juste
            au-dessus, dit la vérité — un point qui palpite laisserait lire une absence comme
            « rien n'est encore arrivé » (§23.0). */}
        <span style={{ fontSize: '11.5px', color: 'var(--ink-400)' }}>
          
          {t('Ils comptent hors ligne et poussent ici tout seuls — zone créée automatiquement si besoin')}
        </span>
      </div>
      {recues.map((z) => (
        <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px', flexWrap: 'wrap' }}>
          <span style={{ width: 28, height: 28, borderRadius: 'var(--r-xs)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="tablet" size={14} stroke={1.9} />
          </span>
          {/* ⚠️ « APPAREIL INCONNU » PLUTÔT QU'UN IDENTIFIANT TECHNIQUE : un terminal que nous n'avons
              pas fourni n'a pas de fiche à la Console, donc pas de nom de magasin. Afficher sa suite
              opaque ne désignerait ni une machine ni une personne. */}
          <span style={{ fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>{z.equipmentCode || t('Appareil inconnu')}</span>
          <span style={{ color: 'var(--ink-600)', flex: 1, minWidth: 180 }}>
            {t('zone {label} — {n} pièce transmise {v}', { label: z.label, n: z.totalQtyMinor, v: heureDeReception(z.submittedAt) })}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 21, padding: '0 8px', borderRadius: 'var(--r-xs)', background: z.attached ? 'var(--teal-100)' : 'var(--amber-bg)', color: z.attached ? 'var(--teal-800)' : 'var(--amber-fg)', fontSize: 'var(--t-micro)', fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {z.attached ? t('Versée') : t('À verser')}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * LE SÉLECTEUR DE ZONE — où l'on compte, et où en est chaque rayon.
 *
 * ⚠️ C'EST LE PICKER DE LA CHARTE, pas un menu maison : il apporte la recherche, la création en deux
 * temps, le clavier complet et la restitution du focus. Ce qui manquait n'était que de la
 * PRÉSENTATION — pastille d'état, pièces, badge — et c'est pour ça que ces trois emplacements ont
 * rejoint le picker lui-même plutôt qu'une copie locale.
 *
 * ⚠️ LE DÉCLENCHEUR EST PLEIN (`tone="brand"`) PARCE QU'IL VIT DANS LE CADRE VERT du bloc de scan :
 * blanc, il se lirait comme un champ de saisie de plus, à côté de celui où l'on bipe.
 *
 * ⚠️ L'ÉTAT SE VOIT SANS LIRE : pastille à gauche, badge à droite. C'est un écran qu'on consulte la
 * douchette en main — « quel rayon me reste-t-il ? » doit se répondre en balayant, pas en lisant.
 *
 * ⚠️ ON CRÉE EN TAPANT, et c'est le geste de la maquette : une zone n'est pas un référentiel à
 * administrer avant de compter — elle naît quand quelqu'un commence un rayon.
 *
 * ⚠️ « CRÉER » ÉCRIT VRAIMENT (corrigé 2026-09-04) : la zone est OUVERTE au serveur, donc sa tuile
 * apparaît aussitôt sous le bloc — et elle SURVIT au rechargement. Auparavant la création ne retenait
 * qu'un libellé à l'écran : aucune tuile, et le nom s'évaporait au premier F5. Une création qu'on croit
 * faite et qui disparaît est pire qu'un refus.
 *
 * ⚠️ DÉFINI AU NIVEAU MODULE (§24.1) : défini pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe, et le champ de recherche perdrait le focus lettre après lettre.
 */
function SelecteurZone({ zones, enCours, valeur, verrouillees, onChoisir, onCreer }: {
  zones: InventoryDetail['countZones']; enCours: string; valeur: string;
  /** Les zones qu'un appareil est en train de compter — visibles, jamais sélectionnables. */
  verrouillees: ReadonlySet<string>;
  onChoisir: (z: string) => void; onCreer: (nom: string) => Promise<string>;
}) {
  const options = useMemo(() => {
    const vues = new Map<string, InventoryDetail['countZones'][number]>();
    for (const z of zones) vues.set(z.label, z);
    const nom = enCours.trim();
    if (nom && !vues.has(nom)) vues.set(nom, { label: nom, pieces: 0, state: 'todo' });
    return [...vues.values()].map((z) => {
      // ⚠️ ELLE RESTE VISIBLE, ET SON ÉTAT LE DIT. La retirer de la liste ferait croire que le rayon
      //    n'est pas pris en charge, et l'on ouvrirait une seconde zone du même nom.
      const bloquee = verrouillees.has(z.label);
      return {
        value: z.label,
        label: z.label,
        dot: bloquee ? ('warn' as const) : TON_ZONE[z.state],
        hint: bloquee ? t('appareil') : t('{pieces} pcs', { pieces: z.pieces }),
        badge: bloquee
          ? { label: t('Verrouillée'), tone: 'warn' as const }
          : { label: t(LIBELLE_ZONE[z.state]), tone: TON_ZONE[z.state] },
      };
    });
  }, [zones, enCours, verrouillees]);

  return (
    <Picker
      id="inv-zone" label={t('Zone')} variant="field-inline" tone="brand"
      allLabel={t('Choisir une zone…')}
      panelWidth={322}
      searchPlaceholder={t('Chercher une zone…')}
      // ⚠️ LE REFUS EST ICI, PAS DANS LE PICKER (arbitrage utilisateur : on ne touche pas au picker).
      //    C'est d'ailleurs son contrat — il RAPPORTE un choix, l'écran DÉCIDE. Mélanger des bips de
      //    douchette dans un comptage d'appareil non transmis produirait un total que ni l'un ni
      //    l'autre ne pourrait expliquer, et l'appareil écraserait tout à son envoi.
      options={options} value={valeur}
      onChange={(z) => {
        if (verrouillees.has(z)) {
          showToast(t('« {z} » est comptée sur un appareil — attendez son envoi pour y biper.', { z }), 'warn', 6000);
          return;
        }
        onChoisir(z);
      }}
      createLabel={t('Créer une zone')}
      onCreate={onCreer}
    />
  );
}

/**
 * L'ÉTAT D'UNE ZONE, en mots et en ton.
 *
 * ⚠️ « TERMINÉE » N'EST DIT QUE D'UNE ZONE SOUMISE PAR UN TERMINAL — c'est le seul endroit où la fin
 * d'un comptage est un FAIT. Au back-office, rien ne déclare qu'un rayon est fini : une zone qui
 * porte des pièces reste « en cours », parce qu'on peut toujours en biper une de plus. Annoncer
 * « terminée » sans signal de fin ferait cesser d'aller compter (§23.0).
 */
const LIBELLE_ZONE: Record<'done' | 'counting' | 'todo', Translatable> = {
  done: key('Terminée'), counting: key('En cours'), todo: key('À faire'),
};
const TON_ZONE: Record<'done' | 'counting' | 'todo', 'ok' | 'warn' | 'neutral'> = {
  done: 'ok', counting: 'warn', todo: 'neutral',
};

/**
 * LA CELLULE DE COMPTAGE — retirer une pièce, saisir une quantité, en ajouter une (SPEC §6).
 *
 * ⚠️ LES DEUX BOUTONS SONT ADDITIFS, LA SAISIE EST ABSOLUE, et ce n'est pas la même chose. « +1 »
 * ajoute une pièce à la zone active quel que soit ce qui a été compté ailleurs ; la saisie REMPLACE ce
 * que porte cette zone. Les confondre était le défaut d'origine (voir ci-dessous).
 *
 * ⚠️ LE CHAMP MONTRE LA ZONE ACTIVE, PAS LE TOTAL (correction 2026-09-05). Il affichait le total
 * toutes zones et écrivait dans la zone active : un article compté 6 en réserve, corrigé à 8 depuis la
 * boutique, donnait 14. On croyait rectifier, on additionnait — et l'écart s'aggravait à chaque
 * correction. Quand les deux nombres diffèrent, le total est rappelé dessous : sans lui, on croirait
 * avoir perdu le comptage des autres zones.
 *
 * ⚠️ SANS ZONE, TOUT EST INERTE, et le survol le dit : un comptage doit dire où il a été fait.
 *
 * ⚠️ DÉFINIE AU NIVEAU MODULE (§24.1) : définie pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe.
 */
/** Le champ de quantite — meme dessin partout ou l'on corrige un comptage a la main. */
const champ: React.CSSProperties = { width: 74, boxSizing: 'border-box', height: 32, padding: '0 8px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none', textAlign: 'right', background: 'var(--paper)', color: 'var(--ink-900)' };

function CelluleComptage({ ligne: r, zone, valeur, onSaisir, onValider, onAjuster }: {
  ligne: InventoryReconRow; zone: string; valeur: string | undefined;
  onSaisir: (v: string) => void; onValider: () => void; onAjuster: (delta: number) => void;
}) {
  const inerte = zone === '';
  const dansZone = r.countedInZoneMinor;
  // ⚠️ RIEN DANS CETTE ZONE SE MONTRE VIDE, PAS « 0 » : « 0 » affirmerait qu'on y a cherché et rien
  //    trouvé, ce qui compte comme un manque à la clôture.
  const affiche = valeur ?? (dansZone == null || dansZone === 0 ? '' : String(dansZone));
  // ⚠️ LE TOTAL PORTE SA PROPRE CONDITION plutôt qu'un booléen à côté : un booléen ne transporte pas
  //    l'affinement de type jusqu'au rendu, et la règle finirait écrite deux fois (Forme A).
  const toutesZones = dansZone != null && r.countedQtyMinor != null && r.countedQtyMinor !== dansZone
    ? r.countedQtyMinor : null;
  // ⚠️ LA LIGNE NOMME SES CONTRÔLES : la cellule est rendue une fois par article, et « Ajouter une pièce » répété sur
  //    chaque ligne ne dit pas laquelle à une aide technique.
  const ligne = [r.name, r.sizeLabel].filter(Boolean).join(' · ');
  // ⚠️ DEUX PHRASES ENTIÈRES, jamais le verbe passé en valeur : traduit à part, « Ajouter » et « {v} une pièce » ne
  //    se recollent pas dans toutes les langues.
  const bouton = (signe: '−' | '+', delta: number) => (
    <Tooltip content={inerte ? t('Choisissez une zone dans la barre de scan : un comptage doit dire où il a été fait.')
      : delta > 0 ? t('Ajouter une pièce dans « {zone} »', { zone }) : t('Retirer une pièce dans « {zone} »', { zone })}>
      <button type="button" disabled={inerte} onClick={() => onAjuster(delta)}
        aria-label={delta > 0 ? t('Ajouter une pièce — {ligne}', { ligne }) : t('Retirer une pièce — {ligne}', { ligne })}
        style={{ width: 26, height: 28, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: inerte ? 'var(--ink-300)' : 'var(--ink-700)', fontSize: 'var(--t-control)', fontWeight: 700, lineHeight: 1, cursor: inerte ? 'not-allowed' : 'pointer' }}>
        {signe}
      </button>
    </Tooltip>
  );

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {bouton('−', -1)}
        <Tooltip content={inerte ? t('Choisissez une zone dans la barre de scan : un comptage doit dire où il a été fait.')
          : t('Remplace ce qui est compté dans « {zone} »', { zone })}>
          <input
            value={affiche}
            onChange={(e) => onSaisir(e.target.value)}
            disabled={inerte}
            onBlur={onValider}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            aria-label={t('Compté dans « {zone} » — {ligne}', { zone, ligne })}
            inputMode="numeric" placeholder="—" style={champ} />
        </Tooltip>
        {bouton('+', 1)}
      </span>
      {/* ⚠️ LE TOTAL NE S'AFFICHE QUE S'IL DIFFÈRE : le rappeler toujours ferait deux nombres à lire sur
          chaque ligne d'un tableau qu'on parcourt à la recherche d'un écart. */}
      {toutesZones != null ? (
        <span style={{ fontSize: '10.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
          {t('{countedQtyMinor} au total, toutes zones', { countedQtyMinor: toutesZones })}</span>
      ) : null}
    </div>
  );
}

/**
 * UN SÉLECTEUR DE RANGEMENT — marque, famille ou saison, avec le poids de chaque valeur.
 *
 * ⚠️ C'EST LE PICKER DE LA CHARTE, pas un `<select>` : la liste peut compter trente marques, et
 * la recherche du picker est alors la seule façon d'en trouver un. Aucun comportement n'est modifié —
 * on ne touche pas au picker pour un écran (arbitrage utilisateur).
 *
 * ⚠️ LE POIDS EST DANS L'OPTION. Sans lui, on choisit à l'aveugle et l'on découvre après coup qu'un
 * marque ne pèse qu'une ligne. Et comme les options viennent du serveur calculées sur la vue,
 * aucune ne mène à un tableau vide (§23.0).
 */
function FiltreRangement({ id, libelle, valeur, options, onChoisir }: {
  id: string; libelle: string; valeur: string; options: InventoryFacet[]; onChoisir: (v: string) => void;
}) {
  // ⚠️ LE POIDS VA DANS 'hint' ET NON DANS UNE PASTILLE : 'dot' du picker est un TON de la charte,
  //    pas une couleur libre. La couleur de la famille vit dans la CELLULE du tableau, là où le SPEC
  //    la demande — la faire entrer ici obligerait à modifier le picker pour un seul écran.
  const items = options.map((o) => ({ value: o.value, label: o.value, hint: String(o.count) }));
  return (
    <Picker
      id={id} label={libelle} variant="field-inline"
      // ⚠️ « TOUS » EST PORTÉ PAR LE CONTRÔLE (allLabel), pas ajouté à la liste : c’est lui qui sait
      //    dire « aucune restriction » à sa façon, et une option maison ferait deux vocabulaires.
      allLabel={t('{libelle} — tous', { libelle })}
      searchPlaceholder={t('Chercher…')}
      options={items} value={valeur} onChange={onChoisir}
    />
  );
}

/**
 * LA TABLE DES NON SCANNÉS (SPEC §7) — ce qui devrait être là et que personne n'a vu.
 *
 * ⚠️ SES COLONNES RÉPONDENT À « OÙ ALLER CHERCHER » : marque, famille et saison sont les poignées
 * par lesquelles on reconnaît un rayon entier oublié. La table générale, elle, répond à « combien
 * d'écart » — question qui n'a pas de sens sur une ligne jamais comptée.
 *
 * ⚠️ LA VALORISATION EST CELLE DU THÉORIQUE, pas de l'écart. Sur ces lignes l'écart vaut zéro (rien
 * n'a été comparé) alors que la clôture s'apprête à remettre tout ce stock à zéro : c'est ce
 * montant-là qui est en jeu, et c'est lui qui décide si l'on va recompter ou exclure.
 *
 * ⚠️ LA CASE MAÎTRESSE NE COCHE QUE LA PAGE, et l'écran le dit ailleurs (« n références de ce filtre
 * ne sont pas affichées »). Laisser croire qu'elle prend tout ferait remettre à zéro ce qu'on croyait
 * exclu — on ne s'en apercevrait qu'après la clôture.
 *
 * ⚠️ DÉFINIE AU NIVEAU MODULE (§24.1) : définie pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe du parent.
 */
function TableNonScannes({ rows, running, selection, onSelection, onExclure, onSortirDuSuivi }: {
  rows: InventoryReconRow[]; running: boolean;
  selection: ReadonlySet<string>; onSelection: (s: ReadonlySet<string>) => void;
  onExclure: (gencode: string, exclure: boolean) => void;
  onSortirDuSuivi: (ligne: InventoryReconRow) => void;
}) {
  const toutes = rows.length > 0 && rows.every((r) => selection.has(r.gencode));
  const basculerTout = () => onSelection(toutes ? new Set() : new Set(rows.map((r) => r.gencode)));
  const basculer = (gencode: string) => {
    const n = new Set(selection);
    if (n.has(gencode)) n.delete(gencode); else n.add(gencode);
    onSelection(n);
  };
  const th: React.CSSProperties = { padding: '9px 12px', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '8px 12px', color: 'var(--ink-700)' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
        <thead>
          <tr style={{ background: 'var(--ink-50)' }}>
            {running ? (
              <th style={{ ...th, width: 34, textAlign: 'center' }}>
                <input type="checkbox" checked={toutes} onChange={basculerTout}
                  aria-label={toutes ? t('Décocher les lignes affichées') : t('Cocher les lignes affichées')}
                  style={{ cursor: 'pointer' }} />
              </th>
            ) : null}
            <th style={{ ...th, textAlign: 'left' }}>{t('Article')}</th>
            <th style={{ ...th, textAlign: 'left' }}>{fieldLabel('brand')}</th>
            <th style={{ ...th, textAlign: 'left' }}>{fieldLabel('family')}</th>
            <th style={{ ...th, textAlign: 'left' }}>{fieldLabel('season')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('Théorique')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('Valorisation')}</th>
            {running ? <th style={{ ...th, textAlign: 'right' }} /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.gencode} style={{ borderTop: '1px solid var(--ink-100)', background: selection.has(r.gencode) ? 'var(--teal-100)' : undefined }}>
              {running ? (
                <td style={{ ...td, textAlign: 'center' }}>
                  <input type="checkbox" checked={selection.has(r.gencode)} onChange={() => basculer(r.gencode)}
                    aria-label={t('Cocher {name}', { name: r.name })} style={{ cursor: 'pointer' }} />
                </td>
              ) : null}
              <td style={td}>
                <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)' }}>{r.name}{r.sizeLabel ? ` · ${r.sizeLabel}` : ''}</span>
                <span style={{ display: 'block', fontSize: '11px', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{r.gencode}</span>
              </td>
              {/* ⚠️ « — » PLUTÔT QU'UNE CASE VIDE : un article sorti du catalogue n'a plus de rangement,
                  et une cellule blanche se lirait comme un oubli d'affichage. */}
              <td style={td}>{r.brand || <span style={{ color: 'var(--ink-300)' }}>—</span>}</td>
              <td style={td}>
                {r.family ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {/* La pastille de couleur de la famille — le même repère qu'en caisse. */}
                    <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: r.familyColor || 'var(--ink-300)', flexShrink: 0 }} />
                    {r.family}
                  </span>
                ) : <span style={{ color: 'var(--ink-300)' }}>—</span>}
              </td>
              <td style={td}>{r.season || <span style={{ color: 'var(--ink-300)' }}>—</span>}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-900)' }}>{r.theoreticalQtyMinor}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {/* ⚠️ Un article sans coût connu ne voit pas son stock chiffré, et on le DIT : afficher
                    0,00 € affirmerait « aucune valeur en jeu » (§23.0). */}
                {r.unitCostFine === 0
                  ? <Tooltip content={t('Aucun coût connu pour cet article — son stock ne peut pas être valorisé.')}><span style={{ color: 'var(--ink-400)' }}>{t('non valorisé')}</span></Tooltip>
                  : formatMoney(r.theoreticalValueCents, { thousandsSep: ' ' })}
              </td>
              {running ? (
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <button onClick={() => onExclure(r.gencode, !r.excluded)}
                      style={{ height: 28, padding: '0 10px', border: `1px solid ${r.excluded ? 'var(--teal-800)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: r.excluded ? 'var(--teal-100)' : 'var(--paper)', color: r.excluded ? 'var(--teal-800)' : 'var(--ink-600)', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer' }}>
                      {r.excluded ? t('Exclu') : t('Exclure')}
                    </button>
                    {/* ⚠️ L'ŒIL BARRÉ EST UN AUTRE GESTE QUE « EXCLURE », ET LA DISTINCTION EST TOUT
                        L'ENJEU (SPEC §7) : « exclure » vaut pour CETTE campagne, « hors suivi » est une
                        décision de FICHE qui vaut pour toutes les suivantes. Les confondre ferait
                        recommencer l'exclusion à chaque inventaire, sur les mêmes retouches et
                        prestations — celles qui partent en négatif justement parce qu'on les compte. */}
                    <Tooltip content={t('Hors suivi de stock — l’article disparaît des états de stock et des inventaires à venir. Réglage de fiche, réversible.')}>
                      <button onClick={() => onSortirDuSuivi(r)} aria-label={t('Sortir « {name} » du suivi de stock', { name: r.name })}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-500)', cursor: 'pointer' }}>
                        <Trait d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22M6.71 6.71C3.6 8.5 2 12 2 12s3 8 10 8a9.7 9.7 0 0 0 5.29-1.71" size={14} stroke={1.9} />
                      </button>
                    </Tooltip>
                  </span>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * UNE LIGNE DU DÉTAIL D'UNE ZONE.
 * ⚠️ `zoneKey` EST LA CLÉ DE RANGEMENT DU COMPTAGE, et elle voyage avec la ligne : un comptage vit
 * sous le libellé de la zone ou sous « zone:<id> » selon qui l'a écrit, et corriger sous la mauvaise
 * clé ajouterait une seconde ligne pour le même article — le total doublerait sans rien dire.
 */
type LigneZone = { gencode: string; name: string; articleCode: string; sizeLabel: string; qtyMinor: number; zoneKey: string };

/**
 * LE DÉTAIL D'UNE ZONE — ce qu'on y a compté, article par article.
 *
 * ⚠️ TRIÉE PAR QUANTITÉ DÉCROISSANTE (SPEC §5) : on ouvre ce détail pour vérifier ce qui PÈSE, pas
 * pour lire un annuaire. L'ordre alphabétique enterrerait les vingt pièces sous les unités.
 *
 * ⚠️ 🔴 ET ELLE TIENT CETTE PROMESSE DEPUIS ICI. La phrase « le comptage reste modifiable jusqu'à la
 * clôture » était posée sous une liste en LECTURE SEULE : vraie, et à l'endroit exact où le geste
 * n'existait pas. Corriger supposait de ressortir, d'aller dans « Écarts », d'y choisir la zone — et
 * surtout que l'article y figure.
 *
 * ⚠️ OR UN ARTICLE JUSTE PAR HASARD N'Y FIGURE PAS. Le cas est réel : théorique 2, scanné 2 par
 * erreur alors qu'il n'y en a qu'un. L'écart vaut zéro, donc l'onglet « Écarts » l'exclut ; il a été
 * compté, donc « Non scannés » l'exclut aussi. **Cette liste-ci était le SEUL endroit qui le montrait,
 * et c'était le seul qui ne permettait pas de le corriger.**
 *
 * ⚠️ MÊME SOURCE QUE LA CARTE : le total de l'entête est la somme des lignes affichées. Deux totaux
 * pour la même zone, et plus personne ne sait lequel fait foi (§5).
 *
 * ⚠️ DÉFINIE AU NIVEAU MODULE (§24.1) : définie pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe du parent.
 */
function ModaleZone({ campaignId, zone: z, modifiable, onCorrige, onClose }: {
  campaignId: string; zone: InventoryZone;
  /** La campagne tourne-t-elle ? Une campagne close ou pas démarrée ne se corrige pas. */
  modifiable: boolean;
  /** Prévient l'écran qu'un comptage a bougé — les tuiles et le rapprochement doivent suivre. */
  onCorrige: () => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<LigneZone[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  const [tour, setTour] = useState(0);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const r = await api.inventoryZoneLines(campaignId, z.id);
        if (vivant) setRows(r.rows);
      } catch (e) { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); }
    })();
    return () => { vivant = false; };
  }, [campaignId, z.id, tour]);

  /**
   * CORRIGE la quantité d'un article DANS CETTE ZONE.
   *
   * ⚠️ ON ÉCRIT SOUS LA CLÉ QUE LA LIGNE PORTE, jamais sous le libellé de la zone : un comptage vit
   * sous le libellé ou sous « zone:<id> » selon qui l'a écrit, et se tromper de clé ajouterait une
   * SECONDE ligne pour le même article — le total de la zone doublerait sans rien dire.
   *
   * ⚠️ ZÉRO EST UNE VALEUR, pas un effacement : « je n'en ai finalement trouvé aucun » est une
   * information de comptage, et elle produit un écart qu'il faut voir.
   */
  const corriger = useCallback(async (ligne: LigneZone) => {
    const brut = (saisie[ligne.gencode] ?? '').trim();
    if (brut === '') return;
    const n = Number(brut);
    if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
      showToast(t('Quantité attendue : un entier positif ou nul.'), 'warn');
      return;
    }
    try {
      await api.countInventory(campaignId, ligne.gencode, n, ligne.zoneKey);
      setSaisie((s) => { const { [ligne.gencode]: _parti, ...reste } = s; return reste; });
      setTour((n) => n + 1);
      onCorrige();
      showToast(t('« {v} » — {n} pièce dans « {label} »', { v: ligne.name || ligne.gencode, n, label: z.label }), 'ok', TOAST_TIMING.defaultMs, 'replace');
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000, 'replace'); }
  }, [campaignId, saisie, z.label, onCorrige]);

  const pieces = (rows ?? []).reduce((sum, r) => sum + r.qtyMinor, 0);
  // ⚠️ Une zone d'appareil OUVERTE n'a rien transmis : son détail vide ne dit pas « rayon vide ».
  const enCoursAppareil = !z.fromBackOffice && z.open;
  const GRILLE = 'minmax(0,1.6fr) 150px 74px';

  return (
    <Modal title={t('Zone « {label} »', { label: z.label })}
      subtitle={enCoursAppareil ? t('Comptage en cours sur l’appareil')
        // ⚠️ DEUX COMPTES, DEUX CLÉS COMPTÉES : le moteur n'accorde que sur `{n}`, donc une phrase qui
        //    porte deux nombres ne peut pas les accorder tous les deux. Ce n'est pas une phrase coupée
        //    — c'est une LISTE de deux groupes nominaux, et le « · » est une mise en page.
        : rows == null ? t('Lecture du comptage…')
          : `${t('{n} référence', { n: rows.length })} · ${t('{n} pièce', { n: pieces })}`}
      icon="box" tone="teal" width="lg" open onClose={onClose}>
      <div style={{ padding: '14px 18px' }}>
        {failure ? (
          <div style={{ fontSize: '12.5px', color: 'var(--rose-fg)', lineHeight: 1.55 }}>
            {t('Le comptage de cette zone n’a pas pu être lu — {failure}', { failure })}
          </div>
        ) : rows == null ? (
          <div style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture du comptage…')}</div>
        ) : enCoursAppareil ? (
          // ⚠️ « L'APPAREIL COMPTE » N'EST PAS « RIEN N'A ÉTÉ COMPTÉ » (SPEC §5). Un Mobile Partner garde son
          //    comptage hors ligne jusqu'à l'envoi : le détail est VIDE alors que le rayon est peut-être
          //    déjà fait. Montrer un vide ferait envoyer quelqu'un recompter par-dessus.
          <div style={{ padding: '18px 14px', textAlign: 'center', fontSize: '12.5px', color: 'var(--amber-fg)', lineHeight: 1.6 }}>
            <strong>{z.equipmentCode || t('Un appareil')}  {t('compte cette zone.')}</strong><br />
            
            {t('Rien ne remonte avant la fin du comptage — son contenu sera visible ici dès l’envoi.')}
          </div>
        ) : rows.length === 0 ? (
          // ⚠️ « Rien de compté » n'est pas « zéro pièce trouvée » : la zone existe, personne n'y a
          //    encore bipé. Les confondre ferait lire un rayon vide.
          <div style={{ padding: '18px 0', textAlign: 'center', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            
            {t('Rien n’a encore été compté dans cette zone.')}
          </div>
        ) : (
          <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRILLE, gap: 10, padding: '8px 12px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              <div>{t('Article')}</div><div>{fieldLabel('gencode')}</div><div style={{ textAlign: 'right' }}>{t('Compté')}</div>
            </div>
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {rows.map((r) => (
                <div key={r.gencode} style={{ display: 'grid', gridTemplateColumns: GRILLE, gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center', fontSize: '12.5px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.name || t('(sans libellé)')}{r.sizeLabel ? ` · ${r.sizeLabel}` : ''}
                    </div>
                    <div style={{ fontSize: '10.5px', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>{r.articleCode}</div>
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--ink-600)', fontFamily: 'var(--font-mono)' }}>{r.gencode}</div>
                  {modifiable ? (
                    <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {/* ⚠️ LA VALEUR ACTUELLE RESTE LISIBLE À CÔTÉ DU CHAMP : un champ prérempli se
                          fait effacer par la première frappe, et on perd ce qu'on était venu comparer. */}
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-900)' }}>{r.qtyMinor}</span>
                      <input value={saisie[r.gencode] ?? ''} inputMode="numeric"
                        aria-label={t('Corriger la quantité de {v}', { v: r.name || r.gencode })} placeholder="…"
                        onChange={(e) => setSaisie((s) => ({ ...s, [r.gencode]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void corriger(r); } }}
                        style={{ width: 52, height: 26, padding: '0 6px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
                    </div>
                  ) : (
                    <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-900)' }}>{r.qtyMinor}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
          {modifiable
            ? t('Le comptage reste modifiable jusqu’à la clôture : tapez la quantité juste et validez par Entrée.')
            : t('Cette campagne ne se corrige plus.')}
        </div>
      </div>
    </Modal>
  );
}

/** Une mesure de l'assistant : ce qu'on regarde, et ce que ça pèse. */
function LigneBilan({ libelle, valeur, detail, ton }: {
  libelle: string; valeur: string; detail?: string; ton?: 'neutre' | 'alerte' | 'bien';
}) {
  const couleur = ton === 'alerte' ? 'var(--amber-fg)'
    : ton === 'bien' ? 'var(--teal-800)' : 'var(--ink-900)';
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--ink-100)' }}>
      <span style={{ fontSize: '12.5px', color: 'var(--ink-600)', flex: 1, minWidth: 0 }}>
        {libelle}
        {detail ? <span style={{ display: 'block', fontSize: '11px', color: 'var(--ink-400)', lineHeight: 1.45 }}>{detail}</span> : null}
      </span>
      <span style={{ fontSize: '15px', fontWeight: 800, color: couleur, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{valeur}</span>
    </div>
  );
}

/**
 * RETROUVER UN ARTICLE (SPEC §4) — où en est-il, et dans quelles zones a-t-il été compté.
 *
 * ⚠️ ELLE RÉPOND « OÙ », PAS « COMBIEN ». Les puces de zone sont la réponse : on ouvre cette
 * recherche pièce en main, pour savoir dans quel rayon aller vérifier. Un total de plus n'apprendrait
 * rien que le rapprochement ne dise déjà.
 *
 * ⚠️ « NON COMPTÉ » S'ACCOMPAGNE DU NOMBRE DE ZONES EN COURS SUR UN APPAREIL. Un Mobile Partner qui compte
 * n'a encore rien transmis : l'article est peut-être déjà dans sa besace. Sans cette phrase, on
 * enverrait recompter un rayon que quelqu'un est en train de faire, et les deux comptages se
 * cumuleraient.
 *
 * ⚠️ LA FRAPPE EST TEMPORISÉE (250 ms) : sans cela, « chemise » lance sept recherches dont six sont
 * périmées avant d'arriver — et la dernière réponse reçue n'est pas forcément la dernière demandée.
 * ⚠️ ET LE NUMÉRO DE DEMANDE EST VÉRIFIÉ AU RETOUR : une réponse lente à « che » ne doit pas écraser
 * celle de « chemise », sans quoi la liste contredit ce qui est tapé.
 *
 * ⚠️ DÉFINIE AU NIVEAU MODULE (§24.1) : définie pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe.
 */
function ModaleRecherche({ campaignId, onOuvrirZone, onClose }: {
  campaignId: string; onOuvrirZone: (zone: string) => void; onClose: () => void;
}) {
  const [terme, setTerme] = useState('');
  const [res, setRes] = useState<InventorySearchResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const champRef = useRef<HTMLInputElement>(null);
  const demande = useRef(0);

  useEffect(() => { champRef.current?.focus(); }, []);

  useEffect(() => {
    const q = terme.trim();
    if (q.length < 2) { setRes(null); setFailure(null); return; }
    const mien = ++demande.current;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const r = await api.searchInventory(campaignId, q);
          if (demande.current === mien) { setRes(r); setFailure(null); }
        } catch (e) {
          if (demande.current === mien) setFailure(e instanceof Error ? e.message : String(e));
        }
      })();
    }, 250);
    return () => clearTimeout(timer);
  }, [campaignId, terme]);

  return (
    <Modal title={t('Retrouver un article')} subtitle={t('Code-barres, code article ou désignation')}
      icon="search" tone="teal" width="lg" open onClose={onClose}>
      <div style={{ padding: '14px 18px' }}>
        <input ref={champRef} value={terme} onChange={(e) => setTerme(e.target.value)}
          placeholder={t('Deux caractères au minimum…')}
          style={{ width: '100%', boxSizing: 'border-box', height: 40, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none' }} />

        {failure ? (
          <div style={{ marginTop: 12, fontSize: '12.5px', color: 'var(--rose-fg)', lineHeight: 1.55 }}>
            {t('La recherche n’a pas abouti — {failure}', { failure })}
          </div>
        ) : terme.trim().length < 2 ? (
          // ⚠️ ON DIT POURQUOI RIEN NE S'AFFICHE. Un panneau vide sous un champ où l'on vient de taper
          //    se lit comme « aucun résultat », et l'on cherche ailleurs un article qui est bien là.
          <div style={{ marginTop: 14, fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.6 }}>
            
            {t('Tapez au moins deux caractères. La recherche porte sur les références figées de cette campagne — un article hors périmètre n’y apparaît pas.')}
          </div>
        ) : res == null ? (
          <div style={{ marginTop: 14, fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Recherche…')}</div>
        ) : res.rows.length === 0 ? (
          <div style={{ marginTop: 14, fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.6 }}>
            {t('Aucune référence de cette campagne ne correspond à « {v} ».', { v: terme.trim() })}</div>
        ) : (
          <>
            <div style={{ marginTop: 12, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', maxHeight: 400, overflowY: 'auto' }}>
              {res.rows.map((r) => (
                <div key={r.gencode} style={{ padding: '10px 12px', borderBottom: '1px solid var(--ink-100)', display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-900)' }}>
                      {r.name || t('(sans libellé)')}
                      {/* ⚠️ COULEUR ET TAILLE FONT PARTIE DE L'IDENTITÉ, pas du détail : c'est ce qui
                          sépare deux lignes de même libellé, et donc ce qu'on est venu chercher. */}
                      {r.color || r.sizeLabel ? (
                        <span style={{ color: 'var(--ink-500)', fontWeight: 600 }}>
                          {' · '}{[r.color, r.sizeLabel].filter(Boolean).join(' · ')}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--ink-400)', fontFamily: 'var(--font-mono)' }}>
                      {r.articleCode} · {r.gencode}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {r.zones.length === 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-500)', fontSize: 'var(--t-note)', fontWeight: 700 }}>
                        
                        {t('non compté')}
                      </span>
                    ) : r.zones.map((z) => (
                      // ⚠️ LA PUCE OUVRE LA ZONE : la question suivante est toujours « et quoi
                      //    d'autre y a-t-il ? ». Sans ce lien, il faudrait refermer, retrouver la
                      //    carte, cliquer.
                      <button key={z.zone} type="button" onClick={() => onOuvrirZone(z.zone)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 24, padding: '0 10px', border: 'none', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer' }}>
                        {z.zone} · {z.qtyMinor}
                      </button>
                    ))}
                    {r.excluded ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 700 }}>
                        {t('exclu')}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {res.truncated > 0 ? (
              <div style={{ marginTop: 8, fontSize: '11.5px', color: 'var(--ink-500)' }}>
                
                {t('Il y a d’autres résultats — précisez la recherche pour les voir.')}
              </div>
            ) : null}
          </>
        )}

        {res != null && res.countingZones > 0 ? (
          <div style={{ marginTop: 12, fontSize: '12px', color: 'var(--amber-fg)', lineHeight: 1.55 }}>
            {t('{n} zone est en cours de comptage sur un appareil — sa quantité n’est pas encore connue, et n’apparaît donc pas ci-dessus.', { n: res.countingZones })}</div>
        ) : null}
      </div>
    </Modal>
  );
}
/**
 * L'ASSISTANT DE CLÔTURE — on REGARDE ce qui va s'écrire, puis on écrit (SPEC §7).
 *
 * ⚠️ TROIS TEMPS PARCE QU'IL Y A TROIS CHOSES À SAVOIR, et qu'elles ne se lisent pas ensemble : ce que
 * l'inventaire a constaté, ce qu'il va EFFACER, et le fait que c'est sans retour. Une confirmation à
 * une ligne demandait de décider du geste le plus irréversible de la page sans avoir vu ses chiffres.
 *
 * ⚠️ LA REMISE À ZÉRO EST UNE INFORMATION, PAS UNE QUESTION (SPEC §7). Un inventaire qui a couvert son
 * périmètre AFFIRME que ce qu'il n'a pas trouvé n'est pas là. La proposer comme une option ferait
 * conserver du stock fantôme sur les références mêmes que l'inventaire devait nettoyer. Ce qui reste
 * ouvert, c'est de RECULER pour exclure — et l'étape le dit.
 *
 * ⚠️ ELLE NE BORNE RIEN. Sur un inventaire à moitié saisi, l'étape 2 annoncera parfois neuf cents
 * remises à zéro : c'est un cas légitime (on s'arrête à la fermeture du magasin) et le montrer vaut
 * mieux qu'un garde-fou qui empêcherait de clôturer.
 *
 * ⚠️ DÉFINI AU NIVEAU MODULE (§24.1) : défini pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe du parent.
 */
function AssistantCloture({ detail, campagne, enCours, onFermer, onValider }: {
  detail: InventoryDetail; campagne: InventoryCampaign; enCours: boolean;
  onFermer: () => void; onValider: () => void;
}) {
  const [etape, setEtape] = useState(0);
  const b = detail.closure;
  const eur = (c: number) => formatMoney(c, { thousandsSep: ' ' });
  // ⚠️ ON NE COMPTE QUE CE QUI SERA ÉCRIT : une ligne juste ne produit aucun mouvement, une exclue non
  //    plus. Annoncer « n écritures » sur le total des lignes gonflerait le geste de tout l'inventaire.
  const ecritures = b.varianceCount + b.zeroingCount;

  const ETAPES = [t('Ce qui a été constaté'), t('Ce qui sera effacé'), t('Confirmation')];

  return (
    <Modal title={t('Clôturer l’inventaire {id} ?', { id: campagne.id })} subtitle={ETAPES[etape]}
      icon="check" tone="teal" width="md" open onClose={onFermer}>
      <div style={{ padding: '14px 18px' }}>
        {/* ⚠️ LE CHEMIN EST VISIBLE DÈS LA PREMIÈRE ÉTAPE : on accepte de lire trois écrans quand on
            sait qu'il y en a trois. Sans ce repère, la deuxième étape se lit comme un obstacle. */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {ETAPES.map((step, i) => (
            <span key={step} style={{ flex: 1, height: 3, borderRadius: 'var(--r-pill)', background: i <= etape ? 'var(--teal-800)' : 'var(--ink-200)' }} />
          ))}
        </div>

        {etape === 0 ? (
          <>
            <LigneBilan libelle={t('Références justes')} valeur={String(b.matchedCount)} ton="bien"
              detail={t('Comptées et conformes au théorique — rien ne s’écrit pour elles.')} />
            <LigneBilan libelle={t('Références en écart')} valeur={String(b.varianceCount)}
              detail={t('Comptées, mais différentes du théorique : ce sont elles qui produisent un ajustement.')} />
            <LigneBilan libelle={t('Pièces trouvées en trop')} valeur={`+${detail.surplusQty}`} ton="bien" />
            <LigneBilan libelle={t('Pièces manquantes')} valeur={`−${detail.missingQty}`} ton="alerte" />
            <LigneBilan libelle={t('Écart valorisé')} valeur={eur(detail.totalDeltaValueCents)}
              ton={detail.totalDeltaValueCents < 0 ? 'alerte' : 'neutre'}
              detail={t('Au coût, sur les références comptées uniquement.')} />
            {detail.liveDuringCampaign ? (
              <p style={{ marginTop: 12, fontSize: '12px', color: 'var(--ink-500)', lineHeight: 1.55 }}>
                
                {t('Le magasin a vendu pendant le comptage. Les écarts tiennent compte des mouvements antérieurs à chaque comptage.')}
              </p>
            ) : null}
          </>
        ) : etape === 1 ? (
          <>
            {/* ⚠️ LA CONSÉQUENCE LA PLUS LOURDE OCCUPE SON PROPRE ÉCRAN. Glissée sous les autres
                chiffres, elle se lit en diagonale — or c'est la seule qui efface du stock réel. */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', marginBottom: 12 }}>
              <span style={{ display: 'flex', paddingTop: 1 }}><Trait d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" stroke={2} /></span>
              <span style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
                <Trans k='<1>Ce qui n’a jamais été bipé passe à zéro.</1> Un inventaire qui a couvert son périmètre affirme que ce qu’il n’a pas trouvé n’est pas là. Ce n’est pas une option : si des références ne pouvaient pas être scannées, revenez les exclure.' parts={[(x) => <strong>{x}</strong>]} /></span>
            </div>
            <LigneBilan libelle={t('Références remises à zéro')} valeur={String(b.zeroingCount)} ton="alerte"
              detail={t('Jamais bipées, non exclues, et il reste du stock à retirer.')} />
            <LigneBilan libelle={t('Pièces retirées du stock')} valeur={String(b.zeroingPieces)} ton="alerte" />
            <LigneBilan libelle={t('Valeur effacée')} valeur={eur(-b.zeroingValueCents)} ton="alerte"
              detail={t('Au coût connu. Les articles sans coût n’y figurent pas.')} />
            <LigneBilan libelle={t('Références exclues')} valeur={String(b.excludedCount)}
              detail={b.excludedCount > 0
                ? t('Elles gardent leur théorique — {v} conservés.', { v: eur(b.excludedValueCents) })
                : t('Aucune exclusion : tout ce qui n’a pas été bipé sera effacé.')} />
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--ink-700)', lineHeight: 1.6 }}>
              {/* ⚠️ TROIS NOMBRES, TROIS ACCORDS : la phrase s'accorde sur le sien (`{n}`), et les deux
                  autres arrivent comme des groupes nominaux DÉJÀ comptés. Les laisser dans la phrase
                  les figerait au pluriel de la phrase. */}
              <Trans k='<1>{n} écriture de stock</1> va être enregistrée : {ajustements} et {remises}. Le rapport d’inventaire sera scellé.'
                vars={{ n: ecritures, ajustements: t('{n} ajustement', { n: b.varianceCount }), remises: t('{n} remise à zéro', { n: b.zeroingCount }) }}
                parts={[(x) => <strong>{x}</strong>]} /></p>
            {/* ⚠️ « NE SE ROUVRIRA PAS » EST LA DERNIÈRE CHOSE LUE, et c'est voulu : c'est le seul point
                qu'on ne peut pas rattraper après coup. */}
            <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.6 }}>
              <Trans k='La campagne sera close et <1>ne se rouvrira pas</1>. Les corrections ultérieures passeront par un mouvement de stock ou par un nouvel inventaire.' parts={[(x) => <strong>{x}</strong>]} /></p>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={() => (etape === 0 ? onFermer() : setEtape((n) => n - 1))} disabled={enCours}
            style={{ height: 36, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: enCours ? 'default' : 'pointer' }}>
            {etape === 0 ? t('Continuer l’inventaire') : t('Retour')}
          </button>
          <span style={{ flex: 1 }} />
          {etape < 2 ? (
            <button onClick={() => setEtape((n) => n + 1)}
              style={{ height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: 'pointer' }}>
              
              {t('Suivant')}
            </button>
          ) : (
            <button onClick={onValider} disabled={enCours}
              style={{ height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: enCours ? 'default' : 'pointer', opacity: enCours ? 0.6 : 1 }}>
              {enCours ? t('Clôture en cours…') : t('Valider et clôturer')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * ABANDONNER — la confirmation par SAISIE, pas par simple clic.
 *
 * ⚠️ POURQUOI TAPER UN MOT plutôt que cliquer « oui ». Un abandon jette des heures de comptage et ne
 * se défait pas. La saisie n'est pas une cérémonie : elle force à LIRE ce qui va disparaître, et elle
 * rend le geste impossible à faire de mémoire musculaire — c'est exactement ce qui distingue une
 * confirmation d'un second clic.
 *
 * ⚠️ ELLE DIT LE CHIFFRE RÉEL, pas « des comptages » : c'est lui qui permet de peser la décision. Une
 * modale qui parle en général ne fait hésiter personne.
 *
 * ⚠️ ET ELLE DIT CE QUI NE BOUGE PAS. « Le stock théorique reste inchangé — aucun mouvement n'a été
 * écrit » est la moitié rassurante, et elle doit rester VRAIE : c'est le serveur qui la garantit, en
 * ne touchant jamais au grand livre (§23.0).
 *
 * ⚠️ DÉFINIE AU NIVEAU MODULE (§24.1) : définie pendant le rendu, React démonterait son sous-arbre à
 * chaque frappe, et le champ de confirmation perdrait le focus lettre après lettre.
 */
const MOT_ABANDON = key('ABANDONNER');

function ModaleAbandon({ campagne: c, comptees, onClose, onConfirmer }: {
  campagne: InventoryCampaign; comptees: number; onClose: () => void; onConfirmer: () => Promise<void>;
}) {
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // ⚠️ CASSE IGNORÉE ET BORDS ROGNÉS : on demande une intention, pas une dictée. Refuser « abandonner »
  //    parce qu'il manque une majuscule ferait chercher la faute au lieu de la décision.
  // ⚠️ LE MOT SE TRADUIT, et c'est le mot TRADUIT qu'on compare : un compte anglais tape le mot qu'il lit, jamais
  //    un mot français qu'on lui demanderait de recopier.
  const mot = t(MOT_ABANDON);
  const arme = saisie.trim().toUpperCase() === mot.toUpperCase();

  const partir = useCallback(async () => {
    if (!arme || enCours) return;
    setEnCours(true);
    setFailure(null);
    try { await onConfirmer(); }
    catch (e) { setFailure(e instanceof Error ? e.message : String(e)); setEnCours(false); }
  }, [arme, enCours, onConfirmer]);

  return (
    // ⚠️ L'ICÔNE, LE SOUS-TITRE ET LE TON DESTRUCTIF VIENNENT DE LA CHARTE : les redessiner ici aurait
    //    posé une quatrième géométrie de modale, et la pastille d'alerte aurait dérivé du reste.
    // ⚠️ LARGEUR « sm » (480 px) ET NON 470 : la charte tient une ÉCHELLE de largeurs, pas des pixels
    //    libres. Inventer une valeur intermédiaire ajouterait la géométrie que la charte existe pour
    //    empêcher — 10 px ne valent pas ça.
    <Modal title={t('Abandonner l’inventaire {id} ?', { id: c.id })}
      subtitle={t('L’inventaire disparaît comme s’il n’avait jamais existé.')}
      icon="alert" tone="rose" width="sm" open onClose={onClose}>
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        <div style={{ padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-700)', lineHeight: 1.6 }}>
          <Trans k='<1>{n}</1> référence comptée et les zones de comptage seront <2>jetées</2>. Le stock théorique reste inchangé — <3>aucun mouvement n’a été écrit</3> : les corrections ne s’écrivent qu’à la clôture.' vars={{ n: comptees }} parts={[(x) => <strong>{x}</strong>, (x) => <strong style={{ color: 'var(--rose-fg)' }}>{x}</strong>, (x) => <strong>{x}</strong>]} /></div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: '12.5px', color: 'var(--ink-700)' }}>
            <Trans k='Pour confirmer, tapez <1>{v}</1>' vars={{ v: mot }} parts={[(x) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--ink-900)' }}>{x}</span>]} />
          </span>
          <input
            autoFocus autoComplete="off" value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void partir(); } }}
            style={{ width: '100%', boxSizing: 'border-box', height: 40, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', fontFamily: 'var(--font-mono)', outline: 'none' }}
          />
        </label>

        {/* ⚠️ L'ÉCHEC RESTE DANS LA MODALE, avec le message RÉEL : refermer sur une erreur laisserait
            croire l'abandon fait, et l'inventaire réapparaîtrait au rafraîchissement suivant. */}
        {failure ? (
          <div style={{ fontSize: '12.5px', color: 'var(--rose-fg)', lineHeight: 1.5 }}>
            {t('L’abandon a échoué — {failure}', { failure })}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
        <button onClick={onClose}
          style={{ height: 38, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-600)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}>
          
          {t('Annuler')}
        </button>
        <span style={{ flex: 1 }} />
        {/* ⚠️ INERTE TANT QUE LE MOT N'EST PAS TAPÉ, et il en a l'air : gris, sans curseur de main. Un
            bouton qui semble cliquable et ne fait rien laisse croire à une panne. */}
        <button onClick={() => void partir()} disabled={!arme || enCours}
          style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: arme ? 'var(--rose-fg)' : 'var(--ink-200)', color: arme ? '#fff' : 'var(--ink-400)', fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: arme && !enCours ? 'pointer' : 'default' }}>
          {enCours ? t('Abandon…') : t('Abandonner l’inventaire')}
        </button>
      </div>
    </Modal>
  );
}

// ── LE RAPPROCHEMENT D'UNE CAMPAGNE ────────────────────────────────────────────────────────────
function Campagne({ id, kinds, onCloture }: { id: string; kinds: Record<string, string>; onCloture: () => Promise<void> }) {
  const [detail, setDetail] = useState<InventoryDetail | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [zone, setZone] = useState('');
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  const [enCours, setEnCours] = useState(false);
  const [vue, setVue] = useState<VueRapprochement>('zones');
  const [page, setPage] = useState(0);
  const [filtre, setFiltre] = useState('');
  /**
   * LES TROIS CLASSEMENTS DE L'ONGLET « NON SCANNÉS » (SPEC §7) — marque, famille, saison.
   *
   * ⚠️ ILS NE PARTENT AU SERVEUR QUE SUR CET ONGLET. Un filtre actif sur un onglet qui ne l'affiche pas
   * serait invisible : on lirait « 3 écarts » en croyant en voir neuf cents, sans rien pour comprendre.
   */
  const [rangement, setRangement] = useState<{ brand: string; family: string; season: string }>(
    { brand: '', family: '', season: '' },
  );
  /**
   * LES LIGNES COCHÉES, par code-barres.
   *
   * ⚠️ ELLE SE VIDE DÈS QUE CE QU'ON VOIT CHANGE (vue, filtre, page). Garder une sélection au travers
   * d'un changement de filtre ferait porter un « Exclure la sélection » sur des lignes qui ne sont plus
   * à l'écran — c'est-à-dire sur une décision que personne n'a prise (§23.0).
   */
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const [zones, setZones] = useState<InventoryZone[]>([]);
  const [vuA, setVuA] = useState<number | null>(null);
  /**
   * CE QU'ON AVAIT DÉJÀ VU — la mémoire qui permet de distinguer une ARRIVÉE d'un simple relevé.
   *
   * ⚠️ ELLE COMMENCE VIDE ET SE REMPLIT EN SILENCE AU PREMIER RELEVÉ (règle utilisateur : l'ouverture
   * de la page n'est JAMAIS un déclencheur). Sans cette précaution, arriver sur un inventaire de six
   * zones ferait surgir six annonces d'événements qui datent parfois de la veille — et l'on cesserait
   * de croire les suivantes, celles qui, elles, viennent d'arriver.
   *
   * ⚠️ UNE RÉFÉRENCE, PAS UN ÉTAT : la comparer ne doit pas provoquer de rendu, et sa mise à jour doit
   * être visible du battement SUIVANT sans attendre un cycle de React.
   */
  const connues = useRef<Map<string, string> | null>(null);
  const [horloge, setHorloge] = useState(Date.now());
  const [scan, setScan] = useState('');
  /**
   * COMBIEN DE BIPS ONT ÉTÉ TENTÉS — le seul emploi de ce compteur est de RENDRE LE FOCUS au champ.
   * ⚠️ Il compte les tentatives, refus compris : après un code refusé, la douchette doit pouvoir
   * repartir tout de suite. Ne le bouger qu'en cas de succès laisserait le curseur ailleurs
   * précisément quand on vient de se tromper.
   */
  const [bipCount, setBipCount] = useState(0);
  const [abandon, setAbandon] = useState(false);
  const [zoneOuverte, setZoneOuverte] = useState<InventoryZone | null>(null);
  const [cloture, setCloture] = useState(false);
  const [recherche, setRecherche] = useState(false);

  // ⚠️ SEULE LA DERNIÈRE LECTURE ÉCRIT LA TABLE : la recherche la relance à chaque frappe — une réponse dépassée
  //    afficherait des lignes que le filtre ne demande plus, ou les totaux d'une AUTRE zone sous le champ de saisie
  //    directe, qui écrit, lui, dans la zone saisie.
  const latestDetail = useLatest();
  const reload = useCallback(async () => {
    const isLatest = latestDetail();
    try {
      setFailure(null);
      // ⚠️ SUR L'ONGLET ZONES ON NE DEMANDE QU'UNE LIGNE : la table n'y est pas rendue, seuls les
      //    compteurs comptent — et eux sont calculés sur l'ensemble quoi qu'il arrive. Tirer deux
      //    cents lignes pour n'en afficher aucune est le genre de gaspillage qu'on ne voit jamais.
      const taille = vue === 'zones' ? TAILLE_ZONES : TAILLE_PAGE;
      const lu = await api.inventory(id, {
        view: VUE_SERVEUR[vue], offset: page * taille, limit: taille, q: filtre.trim(),
        // ⚠️ LA ZONE PART AVEC LA LECTURE : c'est elle qui permet à chaque ligne de dire ce que la
        //    saisie directe y remplacera. Sans elle, le champ afficherait un total et écrirait
        //    ailleurs.
        zone: zone.trim(),
        ...(vue === 'noncomptees' ? rangement : {}),
      });
      if (!isLatest()) return;
      setDetail(lu);
    } catch (e) { if (isLatest()) setFailure(e instanceof Error ? e.message : String(e)); }
  }, [id, vue, page, filtre, rangement, zone, latestDetail]);
  useEffect(() => { void reload(); }, [reload]);
  // ⚠️ CHANGER DE FILTRE REMET À LA PREMIÈRE PAGE ET VIDE LA SÉLECTION. Rester en page 4 d'un résultat
  //    qui n'en a plus qu'une afficherait un tableau VIDE — et on conclurait qu'il n'y a plus rien.
  useEffect(() => { setPage(0); setSelection(new Set()); }, [vue, filtre, rangement]);
  // ⚠️ CHANGER DE VUE OU DE FILTRE REMET À LA PREMIÈRE PAGE. Sans ça, passer de « Tout » (page 4) à
  //    « Écarts » (3 lignes) afficherait un tableau VIDE — et on conclurait qu'il n'y a pas d'écart.
  useEffect(() => { setPage(0); }, [vue, filtre]);

  const c = detail?.campaign ?? null;
  const enMarche = c?.status === 'running';

  /**
   * INTERROGE les zones — c'est le NAVIGATEUR qui tire, le terminal qui pousse au serveur.
   *
   * ⚠️ IL N'Y A AUCUN CANAL DU SERVEUR VERS LA PAGE (ni SSE ni WebSocket dans tout le back-office).
   * L'écran demande donc à intervalle régulier, et DIT quand il a regardé pour la dernière fois. Un
   * bandeau « en direct » laisserait croire qu'une zone absente n'est pas encore arrivée, alors
   * qu'elle peut être là depuis dix minutes (§23.0).
   */
  /**
   * DIRE CE QUI VIENT D'ARRIVER D'UN APPAREIL — et rien d'autre.
   *
   * ⚠️ ELLE SE TAIT AU PREMIER RELEVÉ, par construction : la mémoire est nulle tant qu'on n'a pas
   * regardé une première fois, et ce premier passage ne fait que la remplir.
   * ⚠️ ELLE NE PARLE QUE DES ZONES D'APPAREIL. Une zone bipée au clavier, c'est nous : l'annoncer
   * reviendrait à se faire confirmer son propre geste.
   * ⚠️ ELLE COMPARE UNE SIGNATURE, pas seulement l'existence : un appareil qui pousse une seconde fois
   * dans une zone déjà connue est un ÉVÉNEMENT — c'est même le cas où un comptage en écrase un autre.
   */
  const annoncerArrivees = useCallback((recues: InventoryZone[], memoire: React.MutableRefObject<Map<string, string> | null>) => {
    const neuve = new Map(recues.map((z) => [z.id, signatureZone(z)]));
    const avant = memoire.current;
    memoire.current = neuve;
    const arrivees = zonesArrivees(recues, avant);
    for (const { zone: z, connue } of arrivees) {
      const qui = z.equipmentCode || t('Un appareil');
      showToast(t('{qui} — {n} pièce poussée dans « {label} », {v}', { qui, n: z.totalQtyMinor, label: z.label, v: connue ? t('zone mise à jour') : t('zone créée') }), 'ok', 6000);
    }
    return arrivees.length > 0;
  }, []);

  /**
   * LES ZONES QU'UN APPAREIL COMPTE EN CE MOMENT — par NOM, comme les comptages.
   * ⚠️ Elles n'ont pas encore transmis une seule ligne : y biper mélangerait deux comptages, et
   * l'envoi de l'appareil écraserait le nôtre.
   */
  const verrouillees = useMemo(
    () => new Set(zones.filter((z) => !z.fromBackOffice && z.open).map((z) => z.label)),
    [zones],
  );

  /**
   * ⚠️ LE BATTEMENT NE RAFRAÎCHISSAIT QUE LA MOITIÉ DE L'ÉCRAN, et c'est le défaut que ce renvoi
   * corrige. Les TUILES venaient de `inventoryZones`, mais la barre de progression et les compteurs
   * de références vivent dans `detail`, qui n'est rechargé que sur changement de vue, de page ou de
   * filtre. Une zone poussée par un Mobile Partner faisait donc apparaître sa tuile et son message,
   * pendant que l'avancement restait figé — il fallait recharger la page pour le voir bouger.
   *
   * ⚠️ ON NE RECHARGE QUE SI QUELQUE CHOSE A CHANGÉ. Rejouer `reload()` à chaque battement
   * remplacerait la table sous les doigts de quelqu'un qui saisit une quantité, quatre fois par
   * minute et pour rien.
   *
   * ⚠️ PAR UNE RÉFÉRENCE, et non en dépendance : `reload` se reconstruit à chaque changement de vue,
   * de page ou de filtre. L'inscrire ici ré-armerait le minuteur à chaque frappe dans le champ de
   * recherche, et le battement repartirait de zéro sans jamais arriver à échéance.
   */
  const reloadRef = useRef(reload);
  useEffect(() => { reloadRef.current = reload; }, [reload]);

  // ⚠️ SEUL LE DERNIER RELEVÉ ÉCRIT LES ZONES : le battement, le retour sur l'onglet et un changement d'inventaire en
  //    lancent chacun un — un relevé dépassé reposerait des zones plus anciennes, et les annoncerait comme arrivées.
  const latestZones = useLatest();
  const relireZones = useCallback(async () => {
    const isLatest = latestZones();
    try {
      const r = await api.inventoryZones(id);
      if (!isLatest()) return;
      const duNeuf = annoncerArrivees(r.rows, connues);
      setZones(r.rows);
      setVuA(Date.now());
      if (duNeuf) void reloadRef.current();
    } catch { /* ⚠️ Best-effort ASSUMÉ : un battement raté ne doit pas casser l'écran de comptage.
                 Le repère « vérifié il y a … » cesse d'avancer, ce qui le DIT sans rien interrompre. */ }
  }, [id, annoncerArrivees, latestZones]);

  // ⚠️ LA MÉMOIRE SE VIDE AVEC LA CAMPAGNE : les zones d'un autre inventaire ne sont pas des arrivées,
  //    et les annoncer ferait croire à un appareil qui pousse alors qu'on vient seulement de changer
  //    d'écran.
  useEffect(() => { connues.current = null; }, [id]);

  useEffect(() => {
    if (!enMarche) return;
    void relireZones();
    // ⚠️ ON NE BAT PAS DANS UN ONGLET CACHÉ. Un inventaire laisse la page ouverte des heures : sans
    //    cette garde, chaque onglet oublié interrogerait le serveur quatre fois par minute, pour
    //    personne. On rattrape au retour, ce qui est le moment où quelqu'un regarde.
    const tic = setInterval(() => { if (!document.hidden) void relireZones(); }, CADENCE_RAFRAICHISSEMENT_MS);
    const auRetour = () => { if (!document.hidden) void relireZones(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => { clearInterval(tic); document.removeEventListener('visibilitychange', auRetour); };
  }, [enMarche, relireZones]);

  // L'âge du dernier relevé doit VIEILLIR à l'écran, sinon « vérifié il y a 3 s » y reste une heure.
  useEffect(() => {
    if (!enMarche) return;
    const tic = setInterval(() => setHorloge(Date.now()), 5_000);
    return () => clearInterval(tic);
  }, [enMarche]);

  const rattacher = useCallback(async (z: InventoryZone) => {
    // ⚠️ CE QUI SERA ÉCARTÉ EST DIT AVANT LE GESTE, pas après : une fois versée, la question « et les
    //    lignes hors périmètre ? » n'a plus de réponse utile.
    const ok = await confirm({
      title: t('Verser « {label} » dans le comptage ?', { label: z.label }),
      // ⚠️ DEUX PHRASES ENTIÈRES, jamais une phrase coupée : le compte des lignes et celui des
      //    codes-barres hors périmètre s'accordent chacun de leur côté, et chaque clé reste lisible
      //    seule par un traducteur.
      message: z.outOfScope > 0
        ? `${t('{n} ligne scannée.', { n: z.lineCount })} ${t('⚠️ {n} code-barres n’est PAS dans le périmètre figé de cette campagne : il ne sera pas versé. Vérifiez le rayon, ou ouvrez une campagne qui le couvre.', { n: z.outOfScope })}`
        : t('{n} ligne scannée deviendra le comptage de cette zone. Les lignes d’un même code-barres sont additionnées.', { n: z.lineCount }),
      confirmLabel: t('Verser dans le comptage'),
      tone: 'info',
    });
    if (!ok) return;
    try {
      const b = await api.attachInventoryZone(id, z.id);
      showToast(t('Zone versée — {n} référence comptée{v}', { n: b.counted, v: b.skipped ? t(', {n} hors périmètre écartée', { n: b.skipped }) : '' }), b.skipped ? 'warn' : 'ok', 6000);
      await Promise.all([reload(), relireZones()]);
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, reload, relireZones]);

  const demarrer = useCallback(async () => {
    // ⚠️ IRRÉVERSIBLE, et la confirmation le dit : la photo prise ne se reprend pas. Sans elle, un clic
    //    au mauvais moment fige un stock qu'on n'avait pas fini de préparer.
    const ok = await confirm({
      title: t('Figer le stock théorique maintenant ?'),
      message: t('La photo du stock est prise à cet instant et ne se reprend pas. C’est elle qui servira de base à tout le rapprochement — démarrez une fois le magasin prêt.'),
      confirmLabel: t('Figer et démarrer'),
      tone: 'info',
    });
    if (!ok) return;
    setEnCours(true);
    try {
      const r = await api.startInventory(id);
      showToast(t('Campagne démarrée — {n} référence figée', { n: r.snapshotCount }), 'ok');
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
    finally { setEnCours(false); }
  }, [id, reload]);

  /**
   * CORRIGER D'UNE PIÈCE, EN PLUS OU EN MOINS — le geste de l'onglet Écarts (SPEC §6).
   *
   * ⚠️ ADDITIF ET NON ABSOLU, ce qui le rend juste même quand le reste a été compté ailleurs : « +1 »
   * ajoute une pièce à la zone active, sans jamais toucher aux autres. Une correction absolue devrait
   * savoir laquelle des zones réécrire — et ne le peut pas.
   */
  const ajuster = useCallback(async (gencode: string, delta: number) => {
    try {
      const r = await api.adjustInventory(id, gencode, zone.trim(), delta);
      showToast(t('{v} · {name} — « {v2} » · compté {total} / théorique {theoreticalQtyMinor}', { v: delta > 0 ? '+1' : '−1', name: r.name, v2: zone.trim(), total: r.total, theoreticalQtyMinor: r.theoreticalQtyMinor }), 'ok', TOAST_TIMING.defaultMs, 'replace');
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000, 'replace'); }
  }, [id, zone, reload]);

  const compter = useCallback(async (gencode: string) => {
    const brut = (saisie[gencode] ?? '').trim();
    if (brut === '') return;
    const n = Number(brut);
    if (!Number.isFinite(n) || n < 0) { showToast(t('Quantité attendue : un entier positif ou nul.'), 'warn'); return; }
    try {
      await api.countInventory(id, gencode, Math.round(n), zone.trim());
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, saisie, zone, reload]);

  /**
   * BIPER — chaque code ajoute UNE pièce à la zone en cours.
   *
   * ⚠️ LE RETOUR EST IMMÉDIAT ET NOMMÉ : l'opérateur a la douchette en main et ne regarde pas l'écran.
   *    Ce qu'il perçoit, c'est la ligne de retour au coin de l'œil — elle doit donc dire QUOI a été
   *    compté et COMBIEN on en a, pas seulement « ok ».
   * ⚠️ LE REFUS SE DIT AUSSI, avec le message RÉEL du serveur : un code hors périmètre bipé en silence
   *    ferait scanner une rangée entière pour rien.
   */
  const biper = useCallback(async () => {
    const code = scan.trim();
    if (!code) return;
    setScan('');
    setBipCount((n) => n + 1);
    try {
      const r = await api.bumpInventory(id, code, zone.trim());
      // ⚠️ MODE « REPLACE » : le bip est un GESTE RÉPÉTÉ dont seul le dernier retour compte. Trente
      //    codes bipés d'affilée empileraient trente pilules, dont vingt-neuf périmées avant d'être
      //    lues — et le total qui compte, c'est celui d'après.
      showToast(t('+1 · {name} — « {v} » · compté {total} / théorique {theoreticalQtyMinor}', { name: r.name, v: zone.trim(), total: r.total, theoreticalQtyMinor: r.theoreticalQtyMinor }), 'ok', TOAST_TIMING.defaultMs, 'replace');
      await reload();
    } catch (e) {
      // ⚠️ UN REFUS RESTE PLUS LONGTEMPS À L'ÉCRAN qu'une réussite : la réussite ne demande rien, le
      //    refus demande un geste — et il arrive à quelqu'un qui a la douchette en main et regarde le
      //    rayon, pas l'écran.
      showToast(e instanceof Error ? e.message : String(e), 'err', 7000, 'replace');
    }
  }, [id, scan, zone, reload]);

  /**
   * CRÉER UNE ZONE — elle est ouverte au serveur, puis la grille est relue pour que sa tuile s'affiche.
   *
   * ⚠️ ON REND LE NOM RETENU PAR LE SERVEUR, pas celui qui a été tapé : c'est lui qui deviendra la clé
   * des comptages, et l'écran doit désigner la même chose que la base.
   * ⚠️ L'ÉCHEC REMONTE AU PICKER, qui le montre DANS son panneau sans perdre la frappe — l'avaler ici
   * ferait croire à une zone créée qui n'existe pas.
   */
  const creerZone = useCallback(async (nom: string) => {
    await api.openInventoryZone(id, nom.trim());
    await relireZones();
    await reload();
    return nom.trim();
  }, [id, relireZones, reload]);

  /**
   * RETIRER une zone vide — avec confirmation, parce qu'elle disparaît de la liste de tout le monde.
   *
   * ⚠️ SI ELLE ÉTAIT LA ZONE ACTIVE, ON LA LÂCHE : garder un nom qui n'existe plus ferait biper dans le
   * vide, et le serveur refuserait au premier code — après la douchette, pas avant.
   */
  const retirerZone = useCallback(async (z: InventoryZone, pieces: number) => {
    // ⚠️ LA CONFIRMATION CHIFFRE CE QUI PART (SPEC §5), elle ne demande pas « êtes-vous sûr ». Une zone
    //    vide s'annonce comme telle ; une zone comptée dit combien de pièces sortent du cumul — et dit
    //    aussi ce qui NE BOUGE PAS, parce que c'est la première inquiétude.
    const ok = await confirm({
      title: t('Retirer cette zone ?'),
      message: pieces > 0
        ? t('{n} pièce comptée dans « {label} » sera retirée de l’inventaire. Le stock théorique n’est pas touché — rien ne s’écrit avant la clôture. Un Mobile Partner peut repousser la zone à tout moment.', { n: pieces, label: z.label })
        : t('« {label} » n’a rien compté. Elle disparaîtra de la liste ; on peut la recréer à tout moment.', { label: z.label }),
      confirmLabel: t('Retirer'),
    });
    if (!ok) return;
    try {
      await api.deleteInventoryZone(id, z.id, pieces > 0);
      // ⚠️ SI C'ÉTAIT LA ZONE ACTIVE, ON LA LÂCHE : garder un nom qui n'existe plus ferait biper dans
      //    le vide, et le refus n'arriverait qu'après la douchette.
      setZone((actuelle) => (actuelle.trim() === z.label ? '' : actuelle));
      await relireZones();
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, relireZones, reload]);

  /**
   * ABANDONNER — puis rendre la main à la page, et OUBLIER ce qui pointait vers la campagne.
   *
   * ⚠️ LES ÉTATS LOCAUX SE RÉINITIALISENT : la zone choisie et le filtre désignaient une campagne qui
   * n'existe plus. Les garder ferait biper dans le vide au prochain inventaire, sous un nom de zone
   * hérité d'un travail jeté.
   */
  const abandonner = useCallback(async () => {
    const bilan = await api.abandonInventory(id);
    setAbandon(false);
    setZone('');
    setFiltre('');
    setScan('');
    showToast(t('Inventaire {id} abandonné — {n} comptage jeté, stock inchangé', { id, n: bilan.discarded }), 'ok', 6000);
    await onCloture();
  }, [id, onCloture]);

  /**
   * DÉCLARER une zone terminée, ou la rouvrir.
   * ⚠️ ON RELIT LES ZONES APRÈS : l'état est déclaré au serveur, donc c'est lui qui fait foi — poser
   * l'état à l'écran sans attendre ferait diverger la carte de la base au premier refus.
   */
  const terminerZone = useCallback(async (z: InventoryZone, fini: boolean) => {
    try {
      await api.finishInventoryZone(id, z.id, fini);
      await relireZones();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, relireZones]);

  /**
   * EXCLURE (ou réintégrer) LES LIGNES COCHÉES — celles qu'on a sous les yeux, et elles seules.
   * ⚠️ La sélection se vide APRÈS : laisser cochées des lignes qu'on vient de traiter invite à
   * recommencer le même geste sans s'en apercevoir.
   */
  const exclureSelection = useCallback(async (exclure: boolean) => {
    const codes = [...selection];
    if (codes.length === 0) return;
    try {
      const r = await api.excludeFromInventory(id, codes, exclure, exclure ? reasonToSend(WRITTEN_REASONS.cannotBeScanned) : '');
      setSelection(new Set());
      // ⚠️ DEUX PHRASES COMPLÈTES plutôt qu'un tronc et deux queues : « {n} référence(s) » suivi d'un
      //    morceau accordé ailleurs ne se traduit pas — le traducteur reçoit « exclue(s) » sans savoir
      //    à quoi ça s'accorde, et aucune langue ne garantit cet ordre de mots.
      showToast(exclure
        ? t('{n} référence exclue — elle garde son théorique', { n: r.changed })
        : t('{n} référence réintégrée au comptage', { n: r.changed }), 'ok');
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, selection, reload]);

  /**
   * EXCLURE TOUT CE QUE LE FILTRE DÉSIGNE — au-delà de la page affichée.
   *
   * ⚠️ LE SERVEUR RÉSOUT LE FILTRE, PAS NOUS. Envoyer les codes supposerait de les avoir tous lus,
   * c'est-à-dire de défaire la pagination ; et deux façons de dire « ce qui est filtré » finiraient par
   * désigner deux ensembles différents.
   * ⚠️ CONFIRMATION OBLIGATOIRE AVEC LE NOMBRE : le geste porte sur ce qu'on ne voit pas.
   */
  const exclureTout = useCallback(async () => {
    const total = detail?.total ?? 0;
    const ok = await confirm({
      title: t('Exclure les {n} références de ce filtre ?', { n: total }),
      // DEUX PHRASES ENTIÈRES : la seconde ne s'écrit que si des références sont cachées, et s'accorde sur leur nombre.
      message: t('Les {n} références garderont leur stock théorique à la clôture au lieu d’être remises à zéro.', { n: total })
        + (total > (detail?.rows.length ?? 0) ? ` ${t('{n} d’entre elles ne sont pas affichées.', { n: total - (detail?.rows.length ?? 0) })}` : ''),
      confirmLabel: t('Exclure les {n}', { n: total }),
    });
    if (!ok) return;
    try {
      const r = await api.excludeInventoryByFilter(id, {
        view: VUE_SERVEUR[vue], q: filtre.trim(), ...rangement,
      }, true, reasonToSend(WRITTEN_REASONS.cannotBeScanned));
      setSelection(new Set());
      showToast(t('{n} référence exclue — elle garde son théorique', { n: r.changed }), 'ok', 6000);
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, vue, filtre, rangement, detail, reload]);

  /**
   * SORTIR UN ARTICLE DU SUIVI DE STOCK (SPEC §7) — la décision de catalogue, prise depuis l'inventaire.
   *
   * ⚠️ LA QUESTION SE CHIFFRE AVANT D'ÊTRE POSÉE. Le drapeau est de MODÈLE : cliquer sur une taille
   * emporte toutes les autres. Une confirmation qui ne dirait pas combien de déclinaisons et combien de
   * pièces partent avec serait un pari — et c'est un geste qui retire de la marchandise des états de
   * stock.
   * ⚠️ ELLE DIT AUSSI CE QUI NE CHANGE PAS : l'article reste vendable et compte dans le chiffre
   * d'affaires. Sans cette phrase, « hors suivi » se lit « désactivé », et personne n'ose.
   */
  const sortirDuSuivi = useCallback(async (r: InventoryReconRow) => {
    let apercu: { variants: number; theoreticalQtyMinor: number; name: string };
    try {
      apercu = await api.previewUntrack(id, r.gencode);
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); return; }

    const portee = apercu.variants > 1
      // ⚠️ PLUSIEURS DÉCLINAISONS PAR CONSTRUCTION (la branche du dessous traite l'unique) : le singulier du catalogue est
      //    écrit identique au pluriel — une décision, pas un oubli (Forme DF).
      ? t('Les {n} déclinaisons de « {name} » sortent ensemble — le suivi de stock est un réglage du modèle, pas d’une taille.', { n: apercu.variants, name: apercu.name })
      : t('« {name} » sort du suivi de stock.', { name: apercu.name });
    const ok = await confirm({
      title: t('Sortir cet article du suivi de stock ?'),
      message: t('{portee} Il ne sera plus ni valorisé ni suivi : il disparaît des états de stock, des inventaires et des alertes.', { portee })
        + ' ' + t('Il reste vendable en caisse et compte dans le chiffre d’affaires.')
        + ' ' + t('Dans CET inventaire, il garde son théorique ({n} pièce) au lieu d’être remis à zéro.', { n: apercu.theoreticalQtyMinor })
        + ' ' + t('Réversible depuis la fiche article.'),
      confirmLabel: t('Sortir du suivi'),
    });
    if (!ok) return;
    try {
      // 🔴 UNE CLÉ D'IDEMPOTENCE NE SORT PAS DU NAVIGATEUR TRADUITE (Forme CQ) : le serveur s'en sert
      //    pour reconnaître qu'il a DÉJÀ écrit. Traduite, la même action porterait deux clés selon la
      //    langue du compte, et le garde-fou anti-doublon en attend une.
      const b = await api.untrackFromInventory(id, r.gencode, `untrack-${id}-${r.gencode}`);
      showToast(t('{name} — hors suivi de stock ({n} déclinaison)', { name: apercu.name, n: b.variants }), 'ok', 6000);
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, reload]);

  const basculerExclusion = useCallback(async (gencode: string, exclure: boolean) => {
    try {
      await api.excludeFromInventory(id, [gencode], exclure, exclure ? reasonToSend(WRITTEN_REASONS.cannotBeScanned) : '');
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
  }, [id, reload]);

  /**
   * CLÔTURER — l'écriture elle-même, une fois l'assistant parcouru.
   *
   * ⚠️ LA QUESTION N'EST PLUS ICI : elle a été posée par l'assistant, écran par écran, avec les
   * chiffres. Une confirmation à une ligne demandait de décider du geste le plus irréversible de la
   * page sans avoir vu ce qu'il allait écrire.
   */
  const valider = useCallback(async () => {
    setEnCours(true);
    try {
      const b = await api.closeInventory(id);
      setCloture(false);
      showToast(t('Inventaire validé — {n} ajustement, {remises}{v}', { n: b.adjusted, remises: t('{n} remise à zéro', { n: b.zeroed }), v: b.excluded ? t(', {n} exclue conservée', { n: b.excluded }) : '' }), 'ok', 6000);
      // ⚠️ LA PAGE REPREND LA MAIN : la campagne close quitte la vue vivante et rejoint l'historique.
      //    Se contenter de relire le détail laisserait une session clôturée occuper l'écran, sans plus
      //    aucun geste possible — et cacherait le bouton « Nouvel inventaire ».
      await onCloture();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err', 7000); }
    finally { setEnCours(false); }
  }, [id, onCloture]);


  return (
    <div>
      {failure ? (
        <div style={{ padding: '14px 16px', fontSize: '12.5px', color: 'var(--rose-fg)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)' }}>
          {t('La campagne n’a pas pu être lue — {failure}', { failure })}
        </div>
      ) : !detail || !c ? (
        <div style={{ padding: '14px 16px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture de la campagne…')}</div>
      ) : (
        <>
          {/* ══ L'EN-TÊTE DE SESSION — UNE SEULE CARTE (maquette BO, bloc `invOn`) ══
              ⚠️ RELEVÉE DE LA MAQUETTE, PAS COMPOSÉE. La version précédente éparpillait la même
              information sur trois blocs — un second titre de page sous le premier, un bouton d'action
              flottant à droite, une carte de progression séparée — et l'ensemble ne ressemblait à rien.
              La maquette dit : type, titre, référence et périmètre sur UNE ligne, l'action de sortie au
              bout de cette ligne, la progression juste dessous. C'est celle-là.
              ⚠️ L'ACTION VIT AVEC LE SUIVI DE SAISIE, et c'est un choix, pas une commodité de mise en
              page (demande utilisateur 2026-09-03) : on ne clôture pas dans l'abstrait, on clôture au
              vu de ce qui reste à compter. Les séparer ferait valider sans regarder le reste. */}
          <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: '16px 18px', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={{ height: 24, padding: '0 10px', borderRadius: 'var(--r-xs)', background: tonType(c.kind)[0], color: tonType(c.kind)[1], fontSize: 'var(--t-meta)', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                {kinds[c.kind] ?? c.kind}
              </span>
              {/* ⚠️ LE NOM DONNÉ À L'INVENTAIRE, pas seulement son type : c'est lui qu'on relira sur le
                  procès-verbal, et c'est à lui qu'un magasin reconnaît « son » inventaire du jour. */}
              <span style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)', letterSpacing: '-0.015em' }}>{c.label}</span>
              <span style={{ fontSize: '12.5px', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{c.id}</span>
              <span style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{metaSession(c)}</span>
              <span style={{ flex: 1 }} />
              {c.status === 'prepared' ? (
                <button disabled={enCours} onClick={() => void demarrer()}
                  style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: enCours ? 'default' : 'pointer' }}>
                  
                  {t('Figer le théorique et démarrer')}
                </button>
              ) : (
                <>
                  {/* ⚠️ DESTRUCTIF SECONDAIRE, JAMAIS UN ROUGE PLEIN : le rouge plein est réservé à la
                      CONFIRMATION, dans la modale. Un bouton d'abandon aussi appuyé que la clôture se
                      cliquerait par erreur sur l'écran où l'on travaille le plus vite. */}
                  <button disabled={enCours} onClick={() => setAbandon(true)}
                    style={{ height: 38, padding: '0 16px', border: '1px solid var(--rose)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--rose-fg)', fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: enCours ? 'default' : 'pointer' }}>
                    
                    {t('Abandonner')}
                  </button>
                  <button disabled={enCours} onClick={() => setCloture(true)}
                    style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: enCours ? 'default' : 'pointer' }}>
                    
                    {t('Clôturer l’inventaire')}
                  </button>
                </>
              )}
            </div>

            {/* ⚠️ LA BARRE CONSOMME UN TOTAL CONNU D'AVANCE, elle ne « progresse » pas vers un succès :
                le théorique donne le total, les comptages le consomment, et ce qui reste EST la matière
                des jamais-bipés. D'où le couple vues/total plutôt qu'un pourcentage seul, qui ferait
                lire un avancement de travail (§23.0).
                ⚠️ TANT QUE LA PHOTO N'EST PAS PRISE, IL N'Y A PAS DE DÉNOMINATEUR : la barre laisse la
                place à la phrase qui dit ce qui va se passer — « 0 / 0 » ferait croire à un inventaire
                commencé et bloqué. */}
            {c.status === 'prepared' ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ display: 'flex', color: 'var(--ink-500)', paddingTop: 1 }}><Trait d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8h.01M11 12h1v4h1" stroke={2} size={15} /></span>
                <span style={{ fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55 }}>
                  <Trans k='Rien n’est encore figé. Le stock théorique sera photographié au démarrage — préparez le magasin d’abord, <1>la photo ne se reprend pas</1>.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ height: 10, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                    {/* ⚠️ ELLE SUIT LE MÊME NOMBRE QUE SON LIBELLÉ : remplie sur les références alors
                        que la phrase parle de pièces, elle contredirait le texte juste à côté. */}
                    <div style={{ height: '100%', width: `${Math.round(avancementSession(detail.countedPieces, detail.theoreticalPieces).ratio * 100)}%`, background: 'var(--teal-500)', borderRadius: 'var(--r-pill)', transition: 'width .3s ease' }} />
                  </div>
                </div>
                {/* ⚠️ LES PIÈCES D'ABORD, LES RÉFÉRENCES ENSUITE (SPEC-Inventaire §3). Ce qu'on compte
                    dans un magasin, ce sont des pièces ; le nombre de références dit la couverture du
                    travail, pas la matière. Les deux ensemble répondent aux deux questions qu'on se
                    pose devant la barre : « combien ai-je compté ? » et « où en suis-je ? ». */}
                <span style={{ fontSize: '12.5px', color: 'var(--ink-600)', whiteSpace: 'nowrap' }}>
                  <Trans k='<1>{countedPieces}</1> / {pieces}{v}<2>{countedCount}</2> / {references}' vars={{ countedPieces: detail.countedPieces, pieces: t('{n} pièce comptée', { n: detail.theoreticalPieces }), v: ' · ', countedCount: c.countedCount, references: t('{n} référence vue', { n: c.snapshotCount }) }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></span>
              </div>
            )}
          </div>

          {/* ══ LA BARRE DE SCAN — AU-DESSUS des tuiles, comme la maquette ══
              ⚠️ ELLE N'EST PAS RÉSERVÉE À LA VUE « COMPTAGE PAR ZONE », et c'est le geste qui le
              commande : la douchette est en main quel que soit l'onglet qu'on lit. La reléguer sous une
              seule vue obligerait à revenir en arrière pour biper l'article qu'on vient de trouver en
              regardant les écarts. (La version précédente la posait SOUS les tuiles et sur une seule
              d'entre elles — deux écarts avec la maquette, du même geste.)
              ⚠️ Le sélecteur de zone est passé en « avant » plutôt qu'ajouté au bloc partagé : celui-ci
              ne doit rien savoir des zones, sinon la surface suivante devra y ajouter la sienne. */}
          {c.status === 'running' ? (
            <div style={{ marginBottom: 10 }}>
              {/* ⚠️ PAS DE ZONE, PAS DE COMPTAGE (règle utilisateur 2026-09-04). Un chiffre sans
                  provenance ne se recompte pas : à la première divergence, personne ne sait où aller
                  revérifier — et sur trente mille références, c'est là qu'on se perd.
                  ⚠️ LE CHAMP EST INERTE, ET LE DIT : le laisser actif ferait biper une rangée entière
                  avant de découvrir que le serveur refuse (§23.0). Le serveur refuse quand même — une
                  règle qui ne vit qu'à l'écran est une règle qu'un second appelant ignorera. */}
              {/* ⚠️ LE RETOUR DE BIP EST UN TOAST FLOTTANT, PAS UN BLOC EN FLUX (SPEC §4/§10). Le bloc
                  apparaissait et disparaissait SOUS le champ : à chaque bip, tout ce qui suit — tuiles,
                  zones, table — sautait d'une trentaine de pixels. On compte la douchette en main, en
                  regardant le rayon : l'écran ne doit rien déplacer sous les doigts.
                  ⚠️ ON GARDE LA PILE DE LA CHARTE (3 toasts, effacement automatique) plutôt que le
                  « toast unique persistant » de la maquette : ce comportement-là avait DÉJÀ été relevé
                  comme un défaut §23.0 côté web — un second bip effaçait un message pas encore lu — et
                  il a été corrigé pour toutes les surfaces. Le changer ici le changerait partout. */}
              {/* ⚠️ LA LOUPE VIT AVEC LE SCAN, pas dans un menu : elle répond à la question qu'on se pose
                  la douchette en main — « où est passé cet article ? ». La reléguer ailleurs ferait
                  quitter l'écran de comptage pour y revenir. */}
              <ScanBlock
                value={scan} onChange={setScan} onSubmit={() => void biper()}
                disabled={!zone.trim()}
                placeholder={zone.trim()
                  ? t('Bipez ici — douchette ou saisie du code-barres, Entrée pour compter')
                  : t('Choisissez d’abord une zone — un comptage doit dire où il a été fait')}
                hint={zone.trim() ? t('Chaque bip = +1') : t('Zone requise')}
                before={<SelecteurZone zones={detail?.countZones ?? []} enCours={zone} valeur={zone}
                  verrouillees={verrouillees} onChoisir={setZone} onCreer={creerZone} />}
                focusOn={bipCount}
                after={(
                  <Tooltip content={t('Chercher par code-barres, code article ou désignation, et voir dans quelles zones il a été compté.')}>
                    <button type="button" onClick={() => setRecherche(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 12px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                      <Trait d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35" size={15} stroke={2} />
                      
                      {t('Retrouver un article')}
                    </button>
                  </Tooltip>
                )}
              />
            </div>
          ) : null}

          {/* ══ LES TROIS TUILES (SPEC-Inventaire §5-§7) — des PIÈCES, jamais des lignes ══
              ⚠️ CE QU'ELLES COMPTENT A CHANGÉ LE 2026-09-04, et c'est le fond du sujet : « 12 écarts »
              ne dit pas s'il manque 12 pièces ou 1 200. Un inventaire pèse de la marchandise ; ses
              repères doivent peser la même chose que lui.
              ⚠️ PAS DE TUILE « TOUT » : une référence comptée et conforme n'appelle aucun geste, et la
              lister dilue les deux seules choses actionnables. */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {([
              { v: 'zones', l: t('Comptage par zone'), n: detail.countedPieces },
              { v: 'ecarts', l: t('Écarts'), n: null },
              { v: 'noncomptees', l: t('Non scannés'), n: detail.notCountedPieces },
            ] as { v: VueRapprochement; l: string; n: number | null }[]).map((view) => {
              const on = vue === view.v;
              /**
               * ⚠️ « NON SCANNÉS » PORTE L'AMBRE DÈS QU'IL RESTE DES PIÈCES, même tuile non sélectionnée :
               * c'est l'alerte n°1 du §6.3 — « ça devrait être là, personne ne l'a vu » — et la seule
               * chose que la clôture remettra à zéro toute seule. Neutre, elle passerait pour un compteur.
               */
              const alerte = view.v === 'noncomptees' && (view.n ?? 0) > 0;
              return (
                <button aria-pressed={on} key={view.v} onClick={() => setVue(view.v)} style={{
                  flex: 1, minWidth: 170, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  height: 42, border: `1px solid ${on ? 'var(--ink-900)' : 'var(--ink-200)'}`,
                  borderRadius: 'var(--r-sm)', background: on ? 'var(--ink-900)' : 'var(--paper)',
                  color: on ? 'var(--paper)' : 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 700,
                  whiteSpace: 'nowrap', cursor: 'pointer',
                }}>
                  {view.l}
                  {/* ⚠️ LES ÉCARTS PORTENT DEUX BADGES, PAS UN TOTAL : le surplus et le manque ne se
                      compensent pas. « 0 » sur +38 / −38 dirait « rien à voir » sur un magasin où
                      soixante-seize pièces sont mal placées. */}
                  {view.v === 'ecarts' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 22, height: 22, padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: 'var(--t-meta)', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>+{detail.surplusQty}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 22, height: 22, padding: '0 7px', borderRadius: 'var(--r-pill)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-meta)', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>−{detail.missingQty}</span>
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: 22, height: 22, padding: '0 7px', borderRadius: 'var(--r-pill)',
                      background: alerte ? 'oklch(0.78 0.13 70)' : on ? 'rgba(255,255,255,.18)' : 'var(--ink-100)',
                      color: alerte ? '#0c0e10' : on ? '#fff' : 'var(--ink-600)',
                      fontSize: 'var(--t-meta)', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                    }}>{view.n}</span>
                  )}
                </button>
              );
            })}
          </div>

          {detail.liveDuringCampaign && vue !== 'zones' ? (
            // ⚠️ SANS CETTE PHRASE, LE THÉORIQUE AFFICHÉ NE COLLE PLUS À L'ÉCART. On montre le théorique
            //    FIGÉ (celui du démarrage, que l'opérateur reconnaît) mais on compare au théorique
            //    RAMENÉ au moment du comptage. Un écart de −1 sous un théorique de 8 et un compté de 5
            //    paraîtrait faux : il faut dire que 2 pièces sont parties par la caisse entre-temps.
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', marginBottom: 12 }}>
              <span style={{ display: 'flex', paddingTop: 1 }}><Trait d="M6 2l1.5 3h9L18 2M3 6h18l-2 13H5L3 6zM9 11v5M15 11v5" stroke={2} /></span>
              <span style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
                <strong>{t('Inventaire à chaud — le magasin a vendu pendant le comptage.')}</strong>  {t('Les écarts tiennent compte de ce qui a bougé entre le démarrage et le passage du compteur : ces pièces-là sont parties par la caisse, ce n’est pas de la démarque.')}
                {detail.uncertain > 0 ? <> <Trans k='{n} ligne comptée dans plusieurs zones avec du mouvement entre les deux : sa correction est <1>approximative</1>, et signalée.' vars={{ n: detail.uncertain }} parts={[(x) => <strong>{x}</strong>]} /></> : null}
              </span>
            </div>
          ) : null}

          {detail.sizeInversions.length > 0 && vue === 'ecarts' ? (
            // ⚠️ Une inversion de taille n'est PAS de la démarque : +1 sur une taille et −1 sur une autre
            //    du même modèle, c'est une erreur de scan à la vente. La signaler évite de partir en
            //    chasse d'une perte qui n'a jamais eu lieu.
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', marginBottom: 12 }}>
              <span style={{ display: 'flex', paddingTop: 1 }}><Trait d="M7 4L3 8l4 4M3 8h13M17 20l4-4-4-4M21 16H8" stroke={2} /></span>
              <span style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
                <strong>{t('{n} inversion de taille probable.', { n: detail.sizeInversions.length })}</strong>  {t('Un excédent sur une taille et un manque sur une autre du même modèle : c’est une erreur de scan à la vente, pas une perte.')}
                <br />{detail.sizeInversions.slice(0, 4).map((i) => `${i.articleCode} (${i.underSize ?? '?'} → ${i.overSize ?? '?'}, ${i.qtyMinor})`).join(' · ')}
              </span>
            </div>
          ) : null}

          {vue === 'zones' && c.status === 'running' ? (
            <>
              {/* ⚠️ « VÉRIFIÉ IL Y A … », JAMAIS « EN DIRECT ». La page INTERROGE le serveur, elle n'est
                  pas notifiée : le back-office n'a aucun canal vers le navigateur. Annoncer du direct
                  ferait lire une absence de zone comme « pas encore arrivée » (§23.0). Ajout assumé à la
                  maquette, qui n'a pas ce problème puisqu'elle n'écrit jamais rien. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>{derniereVerification(vuA, horloge)}</span>
                <button onClick={() => void relireZones()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 30, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-600)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer' }}>
                  <Trait d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" size={13} />{t('Vérifier')}
                </button>
              </div>

              {/* ⚠️ LE PLACEHOLDER DIT LES DEUX PORTES, et c'est tout son intérêt (maquette) : on crée la
                  première zone DEPUIS LE SÉLECTEUR de la barre de scan, ou on laisse un Mobile Partner pousser
                  la sienne. « Aucune zone » tout court enverrait chercher un bouton qui n'existe pas —
                  une zone naît d'un geste de comptage, jamais d'un écran d'administration.
                  ⚠️ Trait en POINTILLÉ et fond atténué : un emplacement en attente, pas un incident.
                  ⚠️ L'ICÔNE EST UN REPÈRE DE LIEU, pas un ornement : une zone EST un endroit du magasin
                  (rayon, réserve, vitrine). Elle donne au bloc vide la même silhouette que les autres
                  états vides de l'écran — tuile, titre, phrase — de sorte qu'on le reconnaisse comme
                  « rien ici pour l'instant » avant même d'avoir lu.
                  ⚠️ TON D'ENCRE, PAS DE MARQUE : le turquoise des états vides du reste de l'écran
                  dirait « c'est bien », alors qu'ici il manque quelque chose à faire. */}
              {zones.length === 0 ? (
                <div style={{ border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', padding: '24px 18px', marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 40, height: 40, marginBottom: 5, borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', color: 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Trait d="M12 22s7-7.5 7-13a7 7 0 0 0-14 0c0 5.5 7 13 7 13z" size={19} stroke={1.9} />
                  </span>
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Aucune zone encore')}</span>
                  <span style={{ maxWidth: '46ch', textAlign: 'center', fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.6 }}>
                    <Trans k='Créez la première depuis le sélecteur <1>Zone</1> de la barre de scan, ou laissez les Mobile Partner pousser les leurs.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10, marginBottom: 10 }}>
                  {zones.map((z) => (
                    <CarteZone key={z.id} zone={z} maintenant={horloge} onRattacher={() => void rattacher(z)}
                      active={zone.trim() === z.label}
                      onCompterIci={() => setZone(z.label)}
                      onTerminer={(fini) => void terminerZone(z, fini)}
                      onOuvrir={() => setZoneOuverte(z)}
                      onRetirer={() => void retirerZone(z, z.piecesCounted)} />
                  ))}
                </div>
              )}

              <PanneauReceptions zones={zones} />

              <div style={{ marginBottom: 12 }}>
                <BlocAVenir titre={t('Double comptage à l’aveugle')}>
                  
                  {t('Faire recompter une zone par une seconde personne, sans qu’elle voie le premier passage, puis comparer les deux relevés. C’est la seule façon de distinguer une erreur de comptage d’une vraie démarque — aujourd’hui, un recomptage écrase simplement le précédent.')}
                </BlocAVenir>
              </div>
            </>
          ) : null}

          {/* ⚠️ LE RAPPROCHEMENT N'EST PAS RENDU SUR L'ONGLET ZONES, et il ne l'était déjà pas censé
              l'être : le chargement y demande UNE seule ligne, parce que seuls les compteurs comptent
              ici. Le tableau était pourtant rendu quand même — il affichait donc exactement UNE
              référence, tirée du haut du rapprochement, sous les tuiles de zone. Signalé à l'usage :
              « un tableau avec une référence qui sort de nulle part ». Le commentaire du chargement
              décrivait déjà l'intention ; c'est la condition qui manquait. */}
          {vue === 'zones' ? null : (
          <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
            {/* ⚠️ « NON COMPTÉES » EST UNE VUE À PART, pas un tri : c'est l'alerte « devrait être là
                mais pas scanné » du §6.3. Sur trente mille références, la chercher à l'œil dans le
                tableau complet revient à ne pas l'avoir. */}
            {/* ⚠️ LE SÉLECTEUR DE VUES A ÉTÉ RETIRÉ D'ICI (2026-09-02) : les onglets au-dessus font
                déjà ce travail, avec leurs compteurs globaux. Deux commandes pour le même état, c'est
                l'une des deux qui finira par mentir — et celle-ci proposait encore un « Tout » que la
                maquette ne prévoit pas. Ne reste que le FILTRE, qui, lui, n'a pas de doublon. */}
            {detail.rows.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
                {vue === 'noncomptees' ? (
                  <>
                    {/* ⚠️ LES OPTIONS VIENNENT DU SERVEUR AVEC LEUR POIDS, et chaque liste est calculée
                        SANS son propre filtre mais AVEC les deux autres : aucune option offerte ne mène
                        à un tableau vide, et l'on peut toujours changer de valeur dans un menu qu'on
                        vient d'utiliser (§23.0 — une option qui ne fait rien est une option qui ment). */}
                    <FiltreRangement id="inv-nc-brand" libelle={fieldLabel('brand')} valeur={rangement.brand} options={detail.facets.brand}
                      onChoisir={(v) => setRangement((r) => ({ ...r, brand: v }))} />
                    <FiltreRangement id="inv-nc-family" libelle={fieldLabel('family')} valeur={rangement.family} options={detail.facets.family}
                      onChoisir={(v) => setRangement((r) => ({ ...r, family: v }))} />
                    <FiltreRangement id="inv-nc-season" libelle={fieldLabel('season')} valeur={rangement.season} options={detail.facets.season}
                      onChoisir={(v) => setRangement((r) => ({ ...r, season: v }))} />
                  </>
                ) : null}
                <span style={{ flex: 1 }} />
                <input value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder={t('Filtrer un article…')}
                  style={{ width: 210, height: 34, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '12.5px', outline: 'none', color: 'var(--ink-900)' }} />
              </div>
            ) : null}
            {vue === 'noncomptees' ? (
              <div style={{ padding: 12, borderBottom: '1px solid var(--ink-200)' }}>
                <BlocAVenir titre={t('Contrôle de couverture')}>
                  
                  {t('Sur un inventaire complet, signaler les zones, familles ou rayons qu’AUCUN comptage n’a touchés. La liste ci-dessous dit quelles références manquent ; elle ne dit pas encore qu’un rayon entier a été oublié — c’est pourtant le cas qui coûte le plus cher.')}
                </BlocAVenir>
              </div>
            ) : null}
            {vue === 'noncomptees' && detail.notCounted > 0 ? (
              // ⚠️ LE BANDEAU DIT LA CONSÉQUENCE, PAS LE CONSTAT. « N références non comptées » est une
              //    information ; « la clôture les remettra à zéro » est ce qui fait agir. Sans cette
              //    phrase, on lit la liste comme un avertissement sans suite.
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 14px', background: 'var(--amber-bg)', color: 'var(--amber-fg)', borderBottom: '1px solid var(--ink-200)' }}>
                <span style={{ display: 'flex', paddingTop: 1 }}><Trait d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" stroke={2} /></span>
                <span style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
                  <Trans k='<1>Jamais bipés — la clôture les remettra à zéro, automatiquement.</1> Rien à pointer ici : recomptez une zone oubliée, ou excluez l’impossible à scanner (cartons fermés au dépôt…) — <2>les exclus gardent leur théorique</2>.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></span>
              </div>
            ) : null}
            {vue === 'noncomptees' && selection.size > 0 ? (
              // ⚠️ ELLE DIT COMBIEN, ET SUR QUOI. « Exclure la sélection » sans son compte laisserait
              //    croire que le geste porte sur tout le filtre — sur neuf cents références dont deux
              //    cents sont chargées, la différence ne se découvrirait qu'à la clôture.
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--teal-100)', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--teal-800)' }}>
                  {t('{n} référence cochée', { n: selection.size })}</span>
                <span style={{ flex: 1 }} />
                <button onClick={() => void exclureSelection(true)}
                  style={{ height: 30, padding: '0 12px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
                  
                  {t('Exclure la sélection')}
                </button>
                <button onClick={() => void exclureSelection(false)}
                  style={{ height: 30, padding: '0 12px', border: '1px solid var(--teal-800)', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--teal-800)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>
                  
                  {t('Réintégrer')}
                </button>
                <button onClick={() => setSelection(new Set())}
                  style={{ height: 30, padding: '0 10px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--teal-800)', fontSize: 'var(--t-dense)', fontWeight: 600, cursor: 'pointer' }}>
                  
                  {t('Tout décocher')}
                </button>
              </div>
            ) : null}
            {/* ⚠️ LA PORTE VERS CE QU'ON NE VOIT PAS, et elle ne s'ouvre QUE quand il y a un hors-champ.
                Une case maîtresse ne coche que ce qui est chargé ; sans cette ligne, cocher « tout »
                sur une page de deux cents laisserait sept cents références se faire remettre à zéro. */}
            {vue === 'noncomptees' && c.status === 'running' && detail.total > detail.rows.length ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11.5px', color: 'var(--ink-600)', lineHeight: 1.5 }}>
                  {/* ⚠️ LA PHRASE ENTIÈRE, COMPTE COMPRIS : le nombre rendu à côté d'un fragment ne se
                      traduit pas — aucune langue ne garantit que le nombre vient en premier. */}
                  {t('{n} référence de ce filtre n’est pas affichée.', { n: detail.total - detail.rows.length })}
                </span>
                <button onClick={() => void exclureTout()}
                  style={{ height: 28, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-note)', fontWeight: 700, cursor: 'pointer' }}>
                  {t('Exclure les {n} du filtre', { n: detail.total })}</button>
              </div>
            ) : null}
            {detail.rows.length === 0 ? (
              <EmptyState icon="box" title={t('Aucune référence figée')} note={t('Le théorique sera photographié au démarrage de la campagne.')} />
            ) : vue === 'noncomptees' ? (
              /* ⚠️ UNE TABLE À PART, PAS LA TABLE GÉNÉRALE AVEC DES COLONNES VIDES (SPEC §7). Sur une
                 référence jamais bipée, « Compté » et « Écart » ne peuvent RIEN dire — les afficher
                 remplissait deux colonnes de tirets, et le champ de saisie qu'on y proposait
                 contredisait le bandeau juste au-dessus (« rien à pointer ici »). Ce qu'on veut savoir
                 ici est autre : à quel rayon appartient ce qui manque, et ce que ça vaut. */
              <TableNonScannes rows={detail.rows} running={c.status === 'running'}
                selection={selection} onSelection={setSelection}
                onExclure={(gencode, exclure) => void basculerExclusion(gencode, exclure)}
                onSortirDuSuivi={(r) => void sortirDuSuivi(r)} />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                  <thead>
                    <tr style={{ background: 'var(--ink-50)' }}>
                      {/* ⚠️ UNE SEULE FORME DEPUIS QUE « NON SCANNÉS » A SA PROPRE TABLE (2026-09-04) :
                          cet en-tête portait deux jeux de colonnes selon l'onglet, dont un qui ne
                          s'affichera plus jamais. TypeScript l'a signalé au renommage — un code mort
                          qu'on garde finit par être relu comme s'il servait. */}
                      {[t('Article'), t('Théorique'), t('Compté'), t('En rayon'), t('Écart'), t('Valeur')].map((h, i) => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: i === 0 ? 'left' : 'right', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.rows.map((r) => {
                      const pasCompte = r.countedQtyMinor == null;
                      return (
                        <tr key={r.gencode} style={{ borderTop: '1px solid var(--ink-100)', opacity: pasCompte ? 0.62 : 1 }}>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)' }}>{r.name}{r.sizeLabel ? ` · ${r.sizeLabel}` : ''}</span>
                            <span style={{ display: 'block', fontSize: '11px', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{r.gencode}</span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {r.theoreticalQtyMinor}
                            {/* ⚠️ LA CORRECTION EST MONTRÉE, PAS FONDUE DANS LE CHIFFRE. Remplacer le
                                théorique figé par le théorique corrigé ferait disparaître le nombre que
                                l'opérateur a vu au démarrage — et l'écart deviendrait invérifiable. */}
                            {r.movementsBeforeCount !== 0 ? (
                              <Tooltip label={t("portes ouvertes")} display="block"
                                content={t(r.movementsBeforeCount < 0 ? SORTIES_AVANT : ENTREES_AVANT, { n: Math.abs(r.movementsBeforeCount), theoreticalAtCountMinor: r.theoreticalAtCountMinor })}>
                                <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--blue-fg)', fontWeight: 700 }}>
                                  {r.movementsBeforeCount > 0 ? '+' : '−'}{Math.abs(r.movementsBeforeCount)} → {r.theoreticalAtCountMinor}
                                </span>
                              </Tooltip>
                            ) : null}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            {c.status === 'running' ? (
                              <CelluleComptage ligne={r} zone={zone.trim()}
                                valeur={saisie[r.gencode]}
                                onSaisir={(v) => setSaisie((s) => ({ ...s, [r.gencode]: v }))}
                                onValider={() => void compter(r.gencode)}
                                onAjuster={(d) => void ajuster(r.gencode, d)} />
                            ) : (
                              // ⚠️ LE TIRET EST LA MOITIÉ QUI COMPTE : « — » dit « pas compté », « 0 »
                              //    dirait « rien trouvé ». Les rendre pareil ferait lire une démarque.
                              <span style={{ fontVariantNumeric: 'tabular-nums', color: pasCompte ? 'var(--ink-400)' : 'var(--ink-900)' }}>{quantiteLisible(r.countedQtyMinor)}</span>
                            )}
                          </td>
                          {/* ⚠️ CE N'EST PAS L'ÉCART, ET LA COLONNE LE DIT PAR SA PLACE ET SON TON.
                              Elle répond à « que dois-je trouver si je vais voir ce rayon maintenant ? »
                              — utile pour aller recompter, sans effet sur ce que la clôture écrira. La
                              confondre avec l'écart ferait chercher des pièces qui sont dans le
                              tiroir-caisse. */}
                          {/* ⚠️ L'INFOBULLE ENTRE DANS LA CELLULE, jamais autour : elle rend un
                              <span>, et un <span> entre <tr> et <td> est du HTML invalide que le
                              navigateur remonte HORS du tableau — la colonne disparaîtrait. */}
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.expectedOnShelfMinor != null && r.expectedOnShelfMinor !== r.countedQtyMinor ? 'var(--blue-fg)' : 'var(--ink-500)' }}>
                            <Tooltip content={r.expectedOnShelfMinor != null && r.expectedOnShelfMinor !== r.countedQtyMinor ? t('Des ventes ont eu lieu depuis le comptage — voilà ce qui doit rester en rayon.') : undefined}>
                              <span>{r.expectedOnShelfMinor == null ? '—' : r.expectedOnShelfMinor}</span>
                            </Tooltip>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: r.deltaQtyMinor !== 0 ? 700 : 400, color: r.deltaQtyMinor < 0 ? 'var(--rose-fg)' : r.deltaQtyMinor > 0 ? 'var(--teal-800)' : 'var(--ink-500)' }}>
                            {pasCompte ? '—' : r.deltaQtyMinor > 0 ? `+${r.deltaQtyMinor}` : String(r.deltaQtyMinor)}
                            {r.uncertain ? (
                              <Tooltip label={t('correction approximative')}
                                content={t('Compté dans plusieurs zones avec du mouvement entre les deux : on ne peut pas savoir de quelle zone la pièce est partie.')}>
                                <span style={{ marginLeft: 4, color: 'var(--amber-fg)', cursor: 'help' }}>≈</span>
                              </Tooltip>
                            ) : null}
                          </td>
                          {/* ⚠️ LE BOUTON « EXCLURE » A DÉMÉNAGÉ dans la table des non scannés : c'est
                              le seul onglet où il a un sens, et il y est accompagné du classement qui
                              permet de décider. */}
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-700)', whiteSpace: 'nowrap' }}>
                            {pasCompte ? '—' : r.unitCostFine === 0
                              // ⚠️ Un article sans coût défendable ne voit pas son écart chiffré, et on le
                              //    DIT : afficher 0,00 € affirmerait « aucune valeur perdue » (§23.0).
                              ? (
                                <Tooltip content={t('Aucun coût connu pour cet article — l’écart ne peut pas être valorisé.')}>
                                  <span style={{ color: 'var(--ink-400)' }}>{t('non valorisé')}</span>
                                </Tooltip>
                              )
                              : formatMoney(r.deltaValueCents, { thousandsSep: ' ' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {detail.total > TAILLE_PAGE ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
                {/* ⚠️ ON DIT LA POSITION DANS LE TOTAL, pas seulement « page 3 » : c'est le total qui
                    permet de juger s'il reste beaucoup à regarder, et il vient de l'ensemble. */}
                <span style={{ fontSize: '11.5px', color: 'var(--ink-600)', flex: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {t('{v}–{v2} sur {total}', { v: page * TAILLE_PAGE + 1, v2: Math.min((page + 1) * TAILLE_PAGE, detail.total), total: detail.total })}
                </span>
                <button disabled={page === 0} onClick={() => setPage((n) => Math.max(0, n - 1))}
                  style={{ height: 32, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: page === 0 ? 'var(--ink-300)' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: page === 0 ? 'default' : 'pointer' }}>{t('Précédentes')}</button>
                <button disabled={(page + 1) * TAILLE_PAGE >= detail.total} onClick={() => setPage((n) => n + 1)}
                  style={{ height: 32, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: (page + 1) * TAILLE_PAGE >= detail.total ? 'var(--ink-300)' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: (page + 1) * TAILLE_PAGE >= detail.total ? 'default' : 'pointer' }}>{t('Suivantes')}</button>
              </div>
            ) : null}
          </div>
          )}

          {/* ⚠️ LA PHRASE DE LA MAQUETTE, et elle vaut mieux qu'un repère : ce qui inquiète devant une
              colonne d'écarts, c'est de croire qu'on vient d'écrire quelque chose. Elle dit que non. */}
          {vue === 'ecarts' ? (
            <div style={{ marginTop: 9, fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
              
              {t('Rien ne s’écrit avant la clôture — recomptez et corrigez librement, pièce en main.')}
            </div>
          ) : null}

          {/* ⚠️ LE « À VENIR » DE LA VENTILATION A CHANGÉ DE PLACE, faute de porteur : il vivait sur un
              repère « Écart constaté » que la maquette ne connaît pas (elle n'a AUCUNE rangée de
              repères dans la session — la barre de progression dit déjà vues/total/restantes, et les
              trois tuiles portent leurs comptes). Il rejoint donc la vue qu'il concerne. */}
          {vue === 'ecarts' ? (
            <div style={{ marginTop: 10 }}>
              <BlocAVenir titre={t('Ventilation démarque connue / inconnue')}>
                
                {t('Séparer ce qui est déjà tracé (casse, péremption, don) de ce qui manque sans explication. Le cœur sait faire la distinction ; rien ne lui donne encore les motifs côté back-office, donc le total ci-dessus est un écart BRUT — pas de la démarque inconnue.')}
              </BlocAVenir>
            </div>
          ) : null}
          {recherche ? (
            <ModaleRecherche campaignId={id}
              onOuvrirZone={(nom) => {
                // ⚠️ ON REJOINT LA ZONE PAR SON NOM, pas par un identifiant : les puces viennent des
                //    comptages, qui sont clés par NOM de zone (une zone d'appareil et une zone bipée au
                //    clavier portant le même nom sont la MÊME zone, §5).
                const z = zones.find((x) => x.label === nom);
                setRecherche(false);
                if (z) setZoneOuverte(z);
              }}
              onClose={() => setRecherche(false)} />
          ) : null}
          {cloture ? (
            <AssistantCloture detail={detail} campagne={c} enCours={enCours}
              onFermer={() => setCloture(false)} onValider={() => void valider()} />
          ) : null}
          {zoneOuverte ? (
            <ModaleZone campaignId={id} zone={zoneOuverte} modifiable={c.status === 'running'}
              onCorrige={() => void reload()} onClose={() => setZoneOuverte(null)} />
          ) : null}
          {/* ⚠️ MONTÉE DANS LA SESSION, pas dans la page : elle porte les chiffres de CETTE campagne. */}
          {abandon ? (
            <ModaleAbandon campagne={c} comptees={c.countedCount} onClose={() => setAbandon(false)} onConfirmer={abandonner} />
          ) : null}
        </>
      )}
    </div>
  );
}
